import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

const resources = {
  en: {
    translation: {
      app: {
        brandName: 'RunRate',
      },
      onboarding: {
        brandName: 'RunRate',
        steps: {
          createWorkspace: 'Create Project',
          connectEmail: 'Connect Email',
          selectEmail: 'Select Email',
          connect: 'Connect',
          manualEmailSetup: 'Manual Email Setup',
        },
        descriptions: {
          createWorkspace: 'Set up your first project',
          connectEmail: 'Link your email provider',
          manualEmailSetup: 'Configure email settings',
        },
        workspace: {
          title: 'Create Project',
          description: 'Set up your first project to start tracking deals and tasks.',
          nameLabel: 'Project Name',
          namePlaceholder: 'e.g., Q1 Revenue Growth',
          domainLabel: 'Company Domain',
          domainPlaceholder: 'company.com',
          domainHint: 'Users with this email domain can auto-join your workspace',
          domainFallback: 'domain',
          allowTeammates: 'Allow teammates to join with this domain',
          allowTeammatesWithDomain: 'Allow teammates to join automatically with @{{domain}} email',
          errorEmpty: 'Please enter a project name and domain to continue.',
          errorFailed: 'Failed to create project. Please try again.',
        },
        email: {
          title: 'Connect Email',
          description: 'Sync your inbox to track conversations automatically.',
          providerLabel: 'Email Provider',
          googleWorkspace: 'Google Workspace',
          microsoft365: 'Microsoft 365',
          otherProvider: 'Other Provider',
          skip: 'Skip for now',
          errorFailed: 'Failed to connect. Please try again.',
        },
        // IMAP/SMTP section removed — no backend implementation exists
        buttons: {
          back: 'Back',
          continue: 'Continue',
          connect: 'Connect',
          skip: 'Skip',
          connectAndComplete: 'Connect & Complete',
          connectAndFinish: 'Connect & Finish',
          goToSettings: 'Go to Settings',
          saving: 'Saving...',
          finishSetup: 'Finish Setup',
        },
        messaging: {
          title: 'Add Messaging Channels',
          description: 'Connect WhatsApp and Telegram for unified messaging.',
          whatsappTitle: 'WhatsApp Business',
          whatsappDescription: 'Official API connection',
          telegramTitle: 'Telegram Bot',
          telegramDescription: 'Connect your bot',
          stepLabel: 'Messaging',
          whatsappStep: 'WhatsApp',
          telegramStep: 'Telegram',
          errorFailed: 'Failed to save messaging settings. Please try again.',
        },
        connect: {
          title: 'Connect & Finish',
          description: 'Authorize your email provider to start syncing.',
          providerTitle: 'Connect {{provider}}',
          providerDescription: "We'll sync your emails and calendar to keep everything in one place. You'll be redirected to authorize access.",
        },
      },
      dashboard: {
        greeting: {
          morning: 'Good morning',
          afternoon: 'Good afternoon',
          evening: 'Good evening',
          subtitle: "Here's what's happening today",
        },
        stats: {
          openTasks: 'Open Tasks',
          pipelineValue: 'Pipeline Value',
          openDeals: 'open deals',
          contacts: 'Contacts',
          companies: 'Companies',
          wonDeals: 'Won This Month',
          dealsWon: 'deals won',
          monthlyTarget: 'Monthly target',
        },
        ai: {
          title: 'AI Assistant',
          placeholder: 'Ask Turia to do anything...',
          heroPlaceholder: 'Ask AI to do anything...',
          shortcut: 'Cmd+Enter',
          skillsLabel: 'Skills',
          fastMode: 'Fast mode',
          configure: 'Configure AI',
          send: 'Send',
          skills: {
            draftEmail: 'Draft an email',
            summarize: 'Summarize content',
            createTask: 'Create a task',
            quickReply: 'Generate quick reply',
            research: 'Research a topic',
          },
          prompts: {
            draftEmail: 'Draft an email about: ',
            summarize: 'Summarize the following: ',
            createTask: 'Create a task to: ',
            quickReply: 'Generate a quick reply to: ',
            research: 'Research and tell me about: ',
          },
        },
        metrics: {
          contacts: 'Contacts',
          tasks: 'Open tasks',
          deals: 'Deals',
          revenue: 'Revenue',
          actions: {
            contacts: 'View Contacts',
            tasks: 'View Tasks',
            deals: 'View Deals',
            revenue: 'View Revenue',
          },
        },
        briefing: {
          title: 'Daily Briefing',
          empty: 'No briefing available yet.',
          actions: {
            prepare: 'Prepare for Sync',
            viewProposal: 'View Proposal',
          },
        },
        activity: {
          title: 'Recent Activity',
          viewAll: 'View All',
          empty: 'No recent activity.',
          justNow: 'Just now',
        },
        schedule: {
          title: "Today's Schedule",
          open: 'Open Calendar',
          empty: 'No events scheduled for today.',
          connectHint: 'Connect your calendar to see upcoming events.',
        },
        tasks: {
          title: 'Priority Tasks',
          viewAll: 'View All',
          empty: 'No priority tasks',
          add: 'Add new task',
          priorityNormal: 'Normal',
        },
        actions: {
          title: 'Actions',
          completed: 'Completed',
          viewAll: 'View all',
          empty: 'No actions yet. Ask Turia AI to create one.',
        },
        calendar: {
          yesterday: 'Yesterday',
          empty: 'You have no meetings today',
          viewAll: 'View calendar',
        },
      },
      inbox: {
        title: 'Inbox',
        search: 'Search emails...',
        searchPlaceholder: 'Search...',
        noEmails: 'No emails found',
        noMessagesFound: 'No messages found',
        markAsRead: 'Mark as read',
        markAsUnread: 'Mark as unread',
        refresh: 'Refresh',
        summaryGenerating: 'Generating summary...',
        summaryFailed: 'Failed to generate summary',
        aiNotConfigured: 'AI is not configured for this workspace. Add a Kimi key in Settings → AI.',
        aiEmptyThread: 'There is no text in this thread to analyze yet.',
        aiFailed: 'Unable to generate. Please try again.',
        noMessages: 'No messages yet.',
        messageUnavailable: 'Message content unavailable.',
        noContent: 'No content',
        aiSummary: {
          label: 'AI Summary',
        },
        message: {
          unknownSender: 'Unknown',
          noSubject: '(No subject)',
        },
        tags: {
          important: 'Important',
        },
        folders: {
          inbox: 'Inbox',
          sent: 'Sent',
          archive: 'Archive',
          trash: 'Trash',
        },
        channels: {
          all: 'All channels',
          email: 'Email',
          whatsapp: 'WhatsApp',
          telegram: 'Telegram',
          linkedin: 'LinkedIn',
        },
        actions: {
          turiaInsights: 'Turia Insights',
          createTask: 'Create Task',
          addToCalendar: 'Add to Calendar',
          archive: 'Archive',
          delete: 'Delete',
          reply: 'Reply',
          forward: 'Forward',
          viewProfile: 'Profile',
        },
        deleteConfirm: {
          title: 'Permanently Delete',
          description: 'Are you sure you want to permanently delete "{{subject}}"? This action cannot be undone.',
          thisItem: 'this item',
          confirm: 'Delete Forever',
        },
        summary: {
          title: 'Thread Summary',
          createTask: 'Create Task',
          draftReply: 'Draft Reply',
        },
        reply: {
          title: 'Reply',
          saved: 'Draft saved',
          placeholder: 'Type your reply...',
          drafting: 'Turia is drafting...',
          draftWithTuria: 'Draft with Turia',
          send: 'Send',
          sendFailed: 'Failed to send message. Please try again.',
          quotePrefix: 'Original message:',
          templates: {
            replyPrefix: 'Thanks for the update.',
            forwardHeader: 'Forwarded message',
            forwardFrom: 'From: {{name}} <{{email}}>',
            forwardSubject: 'Subject: {{subject}}',
            forwardDate: 'Date: {{date}}',
          },
        },
        tasks: {
          defaultTitle: 'Follow up: {{subject}}',
        },
        attachments: {
          title: 'Attachments',
          placeholder: 'attachment.pdf',
        },
        contactProfile: {
          title: 'Contact Profile',
          viewProfile: 'View Profile',
          addToCrm: 'Add to CRM',
          relatedTasks: 'Related Tasks',
          noTasks: 'No tasks linked to this contact',
          history: 'History',
          noHistory: 'No history available',
        },
        emptyState: {
          title: 'Select a conversation',
          description: 'Choose a thread from the list to view details and reply.',
        },
        errors: {
          noThreadSelected: 'No thread selected',
        },
      },
      calendar: {
        header: {
          today: 'Today',
          addEvent: 'Add Event',
          view: {
            day: 'Day',
            week: 'Week',
            month: 'Month',
          },
        },
        connectHint: 'Connect your calendar to see events.',
        sidebar: {
          myCalendars: 'My Calendars',
          meetings: 'Meetings',
          tasksDeadlines: 'Tasks & Deadlines',
          holidays: 'Holidays',
          team: 'Team',
        },
        briefing: {
          title: 'Daily Briefing',
          button: 'Turia Briefing',
          empty: 'Your day looks clear. Add events or tasks to see Turia insights.',
        },
        team: {
          empty: 'No team data available.',
        },
        event: {
          create: 'New Event',
          edit: 'Edit Event',
          details: 'Event Details',
          dialogDescription: 'Create or review calendar event details.',
          openOriginal: 'Open in original calendar',
          titlePlaceholder: 'Add title',
          dateTime: 'Date & Time',
          allDay: 'All day',
          start: 'Start',
          end: 'End',
          locationPlaceholder: 'Add location',
          noLocation: 'No location',
          description: 'Description',
          descriptionPlaceholder: 'Add description',
          noDescription: 'No description',
          guests: 'Guests',
          addGuestPlaceholder: 'Add guest email',
          defaultReminders: 'Default calendar reminders',
          customReminders: 'Custom reminders',
          noReminders: 'No reminders',
          visibility: {
            default: 'Default visibility',
            public: 'Public',
            private: 'Private',
          },
          showAs: 'Show as',
          busy: 'Busy',
          free: 'Free',
        },
      },
      kanban: {
        header: {
          workItems: 'Work Items',
        },
        columns: {
          backlog: 'Backlog',
          todo: 'Todo',
          in_progress: 'In Progress',
          done: 'Done',
          cancelled: 'Cancelled',
        },
        priority: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          urgent: 'Urgent',
        },
        actions: {
          edit: 'Edit',
          delete: 'Delete',
          addTask: 'Add task',
          addWorkItem: 'Add Work Item',
          display: 'Display',
          analytics: 'Analytics',
        },
        view: {
          board: 'Board',
          list: 'List',
          gantt: 'Timeline',
        },
        empty: {
          noTasks: 'No tasks yet',
          dropHere: 'Drop tasks here or create a new one',
        },
        gantt: {
          taskLabel: 'Task',
          zoom: 'Zoom',
          day: 'Day',
          week: 'Week',
          month: 'Month',
        },
        labels: {
          unassigned: 'Unassigned',
        },
        bulk: {
          selected: '{{count}} selected',
          clear: 'Clear',
          statusPlaceholder: 'Change status',
          assigneePlaceholder: 'Assign to',
          apply: 'Apply',
          delete: 'Delete',
          deleteTitle: 'Delete tasks?',
          deleteDescription: 'This will delete {{count}} tasks.',
          deleteConfirm: 'Delete',
          unassigned: 'Unassigned',
          selectTask: 'Select task {{id}}',
          selectGroup: 'Select {{status}} tasks',
        },
        badges: {
          bmcp: 'AI',
          bmcpActor: 'AI · {{actor}}',
          bmcpTitle: 'Created via BMCP',
          bmcpTitleActor: 'Created by {{actor}} via BMCP',
        },
        entities: {
          task: 'Task',
        },
      },
      pages: {
        title: 'Pages',
        search: 'Search pages...',
        favorites: 'Favorites',
        allPages: 'All Pages',
        untitled: 'Untitled',
        unsaved: 'Unsaved changes',
        locked: 'Locked',
        titlePlaceholder: 'Untitled page',
        contentPlaceholder: 'Start writing... Type @ to mention people, tasks, or pages',
        removeIcon: 'Remove icon',
        savedAt: 'Saved at {{time}}',
        createdBy: 'Created by {{name}}',
        lastUpdated: 'Last updated {{date}}',
        empty: {
          title: 'No pages yet',
          subtitle: 'Create your first page to get started',
        },
        actions: {
          create: 'New Page',
          edit: 'Edit',
          duplicate: 'Duplicate',
          share: 'Share',
          importMarkdown: 'Import .md',
          favorite: 'Add to favorites',
          unfavorite: 'Remove from favorites',
          lock: 'Lock page',
          unlock: 'Unlock page',
          archive: 'Archive',
        },
        toolbar: {
          bold: 'Bold',
          italic: 'Italic',
          strikethrough: 'Strikethrough',
          h1: 'Heading 1',
          h2: 'Heading 2',
          h3: 'Heading 3',
          bulletList: 'Bullet list',
          orderedList: 'Numbered list',
          taskList: 'Task list',
          quote: 'Quote',
          code: 'Code block',
          link: 'Link',
          image: 'Image',
        },
      },
      deals: {
        header: {
          defaultTitle: 'Sales Pipeline',
          pipelineValue: '{{value}} in pipeline',
        },
        views: {
          kanban: 'Kanban',
          timeline: 'Timeline',
          calendar: 'Calendar',
          coverage: 'Coverage',
        },
        actions: {
          editDeal: 'Edit Deal',
          viewDetails: 'View Details',
          delete: 'Delete',
          addDeal: 'Add Deal',
          newDeal: 'New Deal',
          markWon: 'Mark Won',
          markLost: 'Mark Lost',
          followUp: 'Follow Up',
          followUpTitle: 'Follow up: {{title}}',
        },
        labels: {
          companyId: 'Company #{{id}}',
          owner: 'Owner',
          noDate: 'No date',
          totalValue: '{{value}} total',
          weightedValue: 'Weighted: {{value}}',
          daysSinceActivity: '{{count}} days since activity',
          overdue: 'Overdue',
          aiScore: 'AI {{score}}',
          saving: 'Saving...',
        },
        badges: {
          followUp: 'Follow-up',
        },
        filters: {
          pipeline: 'Pipeline',
          minValue: 'Min value',
          maxValue: 'Max value',
          tagsPlaceholder: 'Tags (comma-separated)',
          owner: 'Owner',
          ownerAll: 'All owners',
          ownerSearch: 'Search owners...',
          ownerEmpty: 'No owners found',
          sortBy: 'Sort by',
          sortOrder: 'Order',
          reset: 'Reset',
          sort: {
            position: 'Position',
            value: 'Value',
            lastActivity: 'Last activity',
            createdAt: 'Created at',
          },
          order: {
            asc: 'Ascending',
            desc: 'Descending',
          },
        },
        errors: {
          loadPipeline: 'Failed to load pipeline. Please try again.',
          moveFailed: 'Failed to move deal. Changes were rolled back.',
        },
        timeline: {
          thisMonth: 'This month',
          invalidRange: 'Invalid date range',
          empty: 'No deals in this range',
          dealCount: '{{count}} deals',
          owner: 'Owner',
        },
        calendar: {
          closing: 'Closing',
          followUps: 'Follow-ups',
          emptyDay: 'No activity',
        },
        coverage: {
          title: 'Pipeline Coverage',
          subtitle: 'Pipeline coverage vs quota target',
          empty: 'Coverage data unavailable',
          quota: 'Quota target',
          current: 'Current pipeline',
          weighted: 'Weighted pipeline',
          ratio: 'Coverage ratio',
          gap: 'Gap to 3x',
          status: {
            healthy: 'Healthy',
            at_risk: 'At risk',
            critical: 'Critical',
          },
        },
        alerts: {
          title: 'Deal risk alerts',
          loading: 'Loading alerts...',
          error: 'Failed to load alerts',
          dismiss: 'Dismiss',
          empty: 'No alerts for the selected filters.',
          filters: {
            label: 'Filter alerts',
            all: 'All alerts',
          },
          summary: {
            critical: '{{count}} critical',
            warning: '{{count}} warning',
            info: '{{count}} info',
          },
          severity: {
            critical: 'Critical',
            warning: 'Warning',
            info: 'Info',
          },
        },
        entities: {
          deal: 'Deal',
        },
      },
      customerInsights: {
        header: {
          title: 'Customer Insights',
          subtitle: 'AI-powered insights to help you understand and engage with customers',
        },
        summary: {
          total: 'Total',
          critical: 'Critical',
          warning: 'Warning',
          info: 'Info',
          dismissed: 'Dismissed',
        },
        filters: {
          typeLabel: 'Type:',
          severityLabel: 'Severity:',
          allTypes: 'All Types',
          type: {
            skeptic_conversion: 'Skeptic Conversion',
            silent_complaint: 'Silent Complaints',
            sales_coaching: 'Sales Coaching',
            competitive_intel: 'Competitive Intel',
          },
          severity: {
            all: 'All',
            critical: 'Critical',
            warning: 'Warning',
            info: 'Info',
          },
        },
        tabs: {
          all: 'All ({{count}})',
          critical: 'Critical ({{count}})',
          warning: 'Warning ({{count}})',
          info: 'Info ({{count}})',
        },
        empty: {
          title: 'No insights found',
          subtitle: 'Try adjusting your filters or check back later',
        },
        emptyBySeverity: {
          critical: 'No critical insights',
          warning: 'No warning insights',
          info: 'No info insights',
        },
        errors: {
          loadingTitle: 'Error loading insights',
          loadingMessage: 'Please try again.',
        },
        details: {
          description: 'Description',
          recommendedActions: 'Recommended Actions',
          confidence: 'Confidence',
          severity: 'Severity',
          category: 'Category',
          created: 'Created',
        },
      },
      contextGraph: {
        header: {
          title: 'Context Graph',
          subtitle: 'Explore relationships and connections in your data',
        },
        actions: {
          resetView: 'Reset View',
          populate: 'Populate Graph',
        },
        stats: {
          nodes: 'Nodes:',
          edges: 'Edges:',
          types: 'Types:',
          loading: '...',
        },
        empty: {
          title: 'No graph data',
          subtitle: 'Click "Populate Graph" to generate nodes from your CRM data',
        },
        pathFinder: {
          title: 'Find Path',
          sourceLabel: 'Source Node',
          sourcePlaceholder: 'Click a node or paste ID',
          targetLabel: 'Target Node',
          targetPlaceholder: 'Enter target node ID',
          findPath: 'Find Path',
          pathFound: 'Path found ({{nodes}} nodes, {{hops}} hops)',
          noPath: 'No path found',
        },
        selectedNode: {
          title: 'Selected Node',
          exploreNeighbors: 'Explore Neighbors',
        },
        legend: {
          title: 'Node Types',
          instructions: {
            click: 'Click: Select node',
            doubleClick: 'Double-click: Explore neighbors',
            drag: 'Drag: Move nodes',
            scroll: 'Scroll: Zoom in/out',
          },
        },
        populateResult: {
          title: 'Graph populated!',
          message: '{{nodes}} nodes, {{edges}} edges',
        },
        nodeTypes: {
          contact: 'Contact',
          deal: 'Deal',
          company: 'Company',
          email: 'Email',
          ticket: 'Ticket',
          event: 'Event',
          decision: 'Decision',
        },
      },
      dailyActions: {
        header: {
          title: 'Daily Actions',
          subtitle: 'AI-prioritized actions for today • {{count}} total',
        },
        actions: {
          refresh: 'Refresh',
          generate: 'Generate Actions',
          generateNew: 'Generate New Actions',
        },
        filters: {
          label: 'Filter:',
          types: {
            all: 'All Actions',
            at_risk: 'At Risk',
            follow_up: 'Follow Up',
            upsell: 'Upsell',
            support: 'Support',
            onboarding: 'Onboarding',
          },
        },
        tabs: {
          all: 'All ({{count}})',
          high: 'High Priority ({{count}})',
          medium: 'Medium Priority ({{count}})',
          low: 'Low Priority ({{count}})',
        },
        empty: {
          title: 'All caught up!',
          subtitle: 'No pending actions at the moment.',
        },
        emptyByPriority: {
          high: 'No high priority actions',
          medium: 'No medium priority actions',
          low: 'No low priority actions',
        },
        errors: {
          loadingTitle: 'Error loading actions',
        },
        snooze: {
          title: 'Snooze Action',
          until: 'Snooze until',
          action: 'Snooze',
        },
      },
      emailDraftEditor: {
        header: {
          title: 'Email Draft Editor',
          subtitle: 'Edit and send AI-generated email drafts',
          aiGenerated: 'AI Generated',
        },
        alerts: {
          sent: 'Email sent successfully!',
          missingRecipient: 'Please enter recipient email',
        },
        errors: {
          loadingTitle: 'Error loading draft',
          notFound: 'Draft not found',
        },
        unavailable: {
          title: 'Draft generation required',
          description:
            'Generate a draft from an inbox thread, contact, or deal action before editing or sending it here.',
        },
        fields: {
          recipient: 'Recipient Email',
          subject: 'Subject',
          body: 'Message Body',
          tone: 'Tone',
          contextType: 'Context Type',
          created: 'Created',
          sent: 'Sent',
        },
        placeholders: {
          recipient: 'recipient@example.com',
          subject: 'Email subject',
          body: 'Email body',
        },
        actions: {
          copy: 'Copy',
          copied: 'Copied',
          send: 'Send Email',
          save: 'Save Draft',
          regenerate: 'Regenerate',
        },
        sidebar: {
          settingsTitle: 'Draft Settings',
          contextTitle: 'Generation Context',
        },
        tone: {
          professional: 'Professional',
          friendly: 'Friendly',
          formal: 'Formal',
          casual: 'Casual',
        },
        contextType: {
          manual: 'Manual',
          thread: 'Thread',
          contact: 'Contact',
          deal: 'Deal',
          ticket: 'Ticket',
        },
      },
      contactDetailSheet: {
        title: 'Contact Overview',
        description: 'Contact details, insights, timeline, and files.',
        actions: {
          openFullPage: 'Open profile',
        },
        errors: {
          load: 'Failed to load contact details.',
        },
        insights: {
          title: 'Turia Insights',
          empty: 'No insights yet. Connect communications to enrich context.',
          highlights: 'Highlights',
          nextActions: 'Next actions',
        },
        timeline: {
          title: 'Timeline',
          empty: 'No activity yet.',
        },
        files: {
          title: 'Files',
          empty: 'No files found.',
          fileFallback: 'File',
          open: 'Open',
        },
      },
      onboardingProgress: {
        header: {
          title: 'Customer Onboarding',
          subtitle: 'Track and manage onboarding progress',
        },
        status: {
          active: 'Active',
          paused: 'Paused',
          completed: 'Completed',
          failed: 'Failed',
        },
        progress: {
          title: 'Overall Progress',
          step: 'Step {{current}} of {{total}}',
        },
        currentStep: {
          label: 'Current Step',
        },
        actions: {
          label: 'Actions:',
          nextStep: 'Next Step',
          pause: 'Pause',
          resume: 'Resume',
          skip: 'Skip Step',
          cancel: 'Cancel Workflow',
        },
        complete: {
          title: 'Onboarding Complete!',
          subtitle: 'Customer has successfully completed all onboarding steps',
        },
        timeline: {
          title: 'Workflow Timeline',
        },
        confirm: {
          cancelTitle: 'Cancel Workflow',
          skipTitle: 'Skip Step',
          cancelDescription:
            'Are you sure you want to cancel this onboarding workflow? This action cannot be undone.',
          skipDescription:
            'Are you sure you want to skip the current step? The workflow will move to the next step.',
        },
        errors: {
          loadingTitle: 'Error loading onboarding workflow',
          notFound: 'Workflow not found',
          missingIds: 'No customer ID or workflow ID provided',
        },
      },
      saasAdminSupport: {
        header: {
          title: 'Support Center',
          subtitle: 'Manage support tickets and FAQs',
        },
        actions: {
          manageFaq: 'Manage FAQ',
          refresh: 'Refresh',
        },
        stats: {
          open: 'Open',
          inProgress: 'In Progress',
          waitingCustomer: 'Waiting Customer',
          slaBreached: 'SLA Breached',
          avgResponse: 'Avg Response',
          total: 'Total',
        },
        filters: {
          searchLabel: 'Search',
          searchPlaceholder: 'Search tickets...',
          statusLabel: 'Status',
          priorityLabel: 'Priority',
          categoryLabel: 'Category',
          assigneeLabel: 'Assignee',
          allStatuses: 'All Statuses',
          allPriorities: 'All Priorities',
          allCategories: 'All Categories',
          allAssignees: 'All Assignees',
          assignedToMe: 'Assigned to me',
        },
        status: {
          open: 'Open',
          in_progress: 'In Progress',
          waiting_customer: 'Waiting Customer',
          waiting_internal: 'Waiting Internal',
          resolved: 'Resolved',
          closed: 'Closed',
        },
        priority: {
          urgent: 'Urgent',
          high: 'High',
          medium: 'Medium',
          low: 'Low',
        },
        table: {
          ticket: 'Ticket',
          status: 'Status',
          priority: 'Priority',
          category: 'Category',
          assignee: 'Assignee',
          source: 'Source',
          created: 'Created',
          assigned: 'Assigned',
          unassigned: 'Unassigned',
        },
        source: {
          email: 'Email',
          chat: 'Chat',
          web: 'Web',
          api: 'API',
        },
        loadingTickets: 'Loading tickets...',
        empty: {
          title: 'No tickets found',
          subtitle: 'Try adjusting your filters',
        },
        fallbacks: {
          empty: '—',
        },
        pagination: {
          page: 'Page {{page}}',
          pageOf: 'Page {{page}} of {{total}}',
          previous: 'Previous',
          next: 'Next',
        },
        errors: {
          loadTickets: 'Unable to load tickets. Please try again.',
        },
      },
      saasAdminSupportDetail: {
        header: {
          title: 'Ticket detail',
          subtitle: 'Review conversation and update status.',
        },
        actions: {
          backToSupport: 'Back to support',
          assignToMe: 'Assign to me',
          saveChanges: 'Save changes',
        },
        labels: {
          ticket: 'Ticket',
          id: 'ID: {{id}}',
          created: 'Created {{date}}',
          category: 'Category: {{category}}',
        },
        fields: {
          status: 'Status',
          priority: 'Priority',
          category: 'Category',
          assignee: 'Assignee',
        },
        placeholders: {
          status: 'Select status',
          priority: 'Select priority',
          category: 'Select category',
        },
        fallbacks: {
          uncategorized: 'Uncategorized',
        },
        conversation: {
          title: 'Conversation',
          loading: 'Loading…',
          count: '{{count}} messages',
          empty: 'No messages yet.',
          internalSuffix: ' · internal',
        },
        reply: {
          title: 'Reply',
          subtitle: 'Send a response to the customer.',
          messageLabel: 'Message',
          internalNote: 'Internal note (not visible to customer)',
          send: 'Send',
        },
        alerts: {
          ticketUpdated: 'Ticket updated.',
          assignedToYou: 'Assigned to you.',
          messageSent: 'Message sent.',
        },
        errors: {
          missingTicketId: 'ticketId missing',
          notAuthenticated: 'Not authenticated',
          ticketNotLoaded: 'Ticket not loaded',
          updateTicket: 'Failed to update ticket',
          assignTicket: 'Failed to assign ticket',
          sendMessage: 'Failed to send message',
          missingMessage: 'Message content is required.',
          ticketNotFound: 'Ticket not found.',
        },
        loadingTicket: 'Loading ticket...',
      },
      companySheet: {
        title: {
          edit: 'Edit Company',
          new: 'New Company',
        },
        description: {
          edit: 'Update the company information below.',
          new: 'Add a new company to your CRM.',
        },
        fields: {
          name: 'Company Name',
          domain: 'Domain',
          industry: 'Industry',
          size: 'Company Size',
          location: 'Location',
          tags: 'Tags',
        },
        placeholders: {
          name: 'Acme Inc.',
          domain: 'acme.com',
          industry: 'Select industry',
          size: 'Select size',
          location: 'San Francisco, CA',
          tags: 'Add a tag',
        },
        options: {
          notSpecified: 'Not specified',
          size: '{{size}} employees',
          industry: {
            technology: 'Technology',
            healthcare: 'Healthcare',
            finance: 'Finance',
            education: 'Education',
            retail: 'Retail',
            manufacturing: 'Manufacturing',
            real_estate: 'Real Estate',
            consulting: 'Consulting',
            marketing: 'Marketing',
            other: 'Other',
          },
        },
        actions: {
          addTag: 'Add',
          save: 'Save Changes',
          create: 'Create Company',
        },
      },
      companyDetailSheet: {
        title: 'Company Details',
        errors: {
          load: 'Unable to load company details.',
        },
        sections: {
          overview: 'Overview',
        },
        fields: {
          domain: 'Domain',
          industry: 'Industry',
          size: 'Company Size',
          location: 'Location',
        },
        fallbacks: {
          notProvided: 'Not provided',
          noTags: 'No tags added yet.',
        },
      },
      contactSheet: {
        title: {
          edit: 'Edit Contact',
          new: 'New Contact',
        },
        description: {
          edit: 'Update the contact information below.',
          new: 'Add a new contact to your CRM.',
        },
        fields: {
          name: 'Name',
          email: 'Email',
          phone: 'Phone',
          company: 'Company',
          tags: 'Tags',
        },
        placeholders: {
          name: 'John Doe',
          email: 'john@example.com',
          phone: '+1 (555) 123-4567',
          company: 'Select a company',
          tags: 'Add a tag',
        },
        options: {
          noCompany: 'No company',
        },
        actions: {
          addTag: 'Add',
          save: 'Save Changes',
          create: 'Create Contact',
        },
      },
      dealSheet: {
        title: {
          edit: 'Edit Deal',
          new: 'New Deal',
        },
        description: {
          edit: 'Update the deal information below.',
          new: 'Create a new deal in your pipeline.',
        },
        fields: {
          title: 'Deal Title',
          value: 'Deal Value',
          currency: 'Currency',
          pipeline: 'Pipeline',
          stage: 'Stage',
          contact: 'Contact',
          company: 'Company',
          expectedClose: 'Expected Close Date',
        },
        placeholders: {
          title: 'Enterprise software license',
          value: '10000',
          pipeline: 'Select pipeline',
          stage: 'Select stage',
          contact: 'Select contact',
          company: 'Select company',
        },
        options: {
          noContact: 'No contact',
          noCompany: 'No company',
        },
        actions: {
          save: 'Save Changes',
          create: 'Create Deal',
        },
        score: {
          title: 'AI deal score',
          loading: 'Calculating score...',
          confidence: 'Confidence',
          lastCalculated: 'Last calculated: {{date}}',
          labels: {
            engagement: 'Engagement',
            fit: 'Fit',
            intent: 'Intent',
            similarity: 'Similarity',
          },
        },
        nextAction: {
          title: 'Next best action',
          loading: 'Generating recommendation...',
          empty: 'No recommendation available yet.',
          confidence: 'Confidence',
          reasoning: 'Reasoning',
          suggestedContent: 'Suggested draft',
          copyDraft: 'Copy draft',
          copied: 'Copied',
          priority: {
            high: 'High',
            medium: 'Medium',
            low: 'Low',
          },
          actions: {
            send_followup: 'Send follow‑up',
            schedule_demo: 'Schedule demo',
            send_proposal: 'Send proposal',
            introduce_champion: 'Introduce champion',
            share_case_study: 'Share case study',
            address_objection: 'Address objection',
            offer_discount: 'Offer discount',
          },
        },
      },
      taskSheet: {
        title: {
          edit: 'Edit Task',
          new: 'New Task',
        },
        description: {
          edit: 'Update the task information below.',
          new: 'Create a new task to track your work.',
        },
        fields: {
          title: 'Title',
          description: 'Description',
          status: 'Status',
          state: 'State',
          priority: 'Priority',
          dueDate: 'Due Date',
          epic: 'Epic',
          sprint: 'Sprint',
          labels: 'Labels',
          watchers: 'Watchers',
          subtasks: 'Subtasks',
          timeEntries: 'Time Entries',
          relationships: 'Linked Tasks',
          blocks: 'Dependencies',
        },
        placeholders: {
          title: 'Follow up with client',
          description: 'Add more details about this task...',
        },
        status: {
          backlog: 'Backlog',
          todo: 'To Do',
          inProgress: 'In Progress',
          done: 'Done',
          cancelled: 'Cancelled',
        },
        priority: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          urgent: 'Urgent',
        },
        actions: {
          save: 'Save Changes',
          create: 'Create Task',
        },
        templates: {
          hint: 'Start with a template or create from scratch',
          skipLink: 'Skip and create blank task',
        },
        tabs: {
          details: 'Details',
          comments: 'Comments',
          links: 'Links',
          attachments: 'Attachments',
          activity: 'Activity',
        },
      },
      taskDetail: {
        comments: {
          empty: 'No comments yet. Be the first to comment!',
          placeholder: 'Write a comment...',
        },
        links: {
          empty: 'No links attached yet.',
          add: 'Add Link',
          urlPlaceholder: 'https://...',
        },
        attachments: {
          empty: 'No attachments yet.',
          add: 'Add Attachment',
          filePath: 'File URL',
          filePathPlaceholder: 'https://files.example.com/report.pdf',
          fileName: 'File name',
          fileNamePlaceholder: 'report.pdf',
          fileSize: 'File size',
          fileSizePlaceholder: 'Size in MB',
          mimeType: 'MIME type',
          mimeTypePlaceholder: 'application/pdf',
          description: 'Description',
          descriptionPlaceholder: 'Optional notes about this file',
        },
        watchers: {
          title: 'Watchers',
          count: '{{count}} watching',
          watch: 'Watch task',
          unwatch: 'Unwatch',
          empty: 'No watchers yet.',
          unknownUser: 'Unknown user',
          notificationLabel: 'Notifications',
          expirationLabel: 'Expiration',
          notifications: {
            all: 'All updates',
            mentions: 'Mentions only',
            status_only: 'Status changes only',
          },
          expiration: {
            never: 'Never',
            oneDay: '1 day',
            sevenDays: '7 days',
            thirtyDays: '30 days',
            custom: 'Custom',
          },
          expiresAt: 'Expires {{date}}',
        },
        timeEntries: {
          total: '{{hours}}h logged',
          add: 'Add time',
          empty: 'No time entries yet.',
          hoursPlaceholder: 'Hours (e.g. 1.5)',
          loggedAtPlaceholder: 'Logged at',
          notePlaceholder: 'What did you work on?',
          entryTitle: '{{hours}}h',
        },
        activity: {
          empty: 'No activity recorded yet.',
        },
        subtasks: {
          add: 'Add subtask',
          placeholder: 'What needs to be done?',
          progress: '{{done}} of {{total}} done',
        },
        labels: {
          add: 'Add label',
          placeholder: 'Enter label name',
          empty: 'No labels',
        },
        relationships: {
          empty: 'No linked tasks',
          add: 'Add relationship',
          typePlaceholder: 'Select type',
          targetPlaceholder: 'Target task ID',
          descriptionPlaceholder: 'Optional description',
          types: {
            blocks: 'Blocks',
            blocked_by: 'Blocked by',
            relates_to: 'Related to',
            duplicates: 'Duplicates',
            duplicated_by: 'Duplicated by',
            parent_of: 'Parent of',
            child_of: 'Child of',
          },
        },
      },
      ticketSheet: {
        title: {
          edit: 'Edit Ticket',
          new: 'New Ticket',
        },
        description: {
          edit: 'Update the support ticket information.',
          new: 'Create a new support ticket.',
        },
        fields: {
          subject: 'Subject',
          description: 'Description',
          status: 'Status',
          priority: 'Priority',
          requesterEmail: 'Requester Email',
        },
        placeholders: {
          subject: 'Brief description of the issue',
          description: 'Provide more details about the issue...',
          requesterEmail: 'customer@example.com',
        },
        actions: {
          save: 'Save Changes',
          create: 'Create Ticket',
        },
      },
      booking: {
        title: 'Appointment Booking',
        subtitle: 'Let clients book time with you — share your link, RunRate handles the rest.',
        min: 'min',
        tabs: { eventTypes: 'Event Types', availability: 'Availability', bookings: 'Bookings' },
        weekdays: { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' },
        status: { confirmed: 'Confirmed', pending: 'Pending', cancelled: 'Cancelled', rescheduled: 'Rescheduled', no_show: 'No-show' },
        eventTypes: {
          hint: 'Meeting templates guests can book.',
          new: 'New event type',
          titleLabel: 'Title',
          titlePlaceholder: '30-min Discovery Call',
          slugLabel: 'Slug',
          durationLabel: 'Duration (min)',
          created: 'Event type created',
          empty: 'No event types yet. Create one and share its link.',
          inactive: 'Inactive',
          copyLink: 'Copy link',
          linkCopied: 'Copied!',
          deleteTitle: 'Delete event type?',
          deleteDescription: '“{{title}}” will stop accepting new bookings. Existing bookings are kept.',
        },
        availability: {
          hint: 'Working-hours profiles used by your event types.',
          newDefault: 'Add working hours',
          defaultName: 'Working Hours',
          created: 'Schedule created',
          empty: 'No availability yet. Add your working hours.',
          default: 'Default',
        },
        bookings: { empty: 'No bookings yet.' },
      },
      publicBooking: {
        notFound: 'This booking page does not exist or is no longer active.',
        pickSlot: 'Pick a time',
        prevWeek: 'Previous week',
        nextWeek: 'Next week',
        noSlots: 'No free slots this week — try the next one.',
        yourDetails: 'Your details',
        name: 'Name',
        email: 'Email',
        confirm: 'Confirm booking',
        bookError: 'That slot was just taken or booking failed. Pick another time.',
        confirmedTitle: 'You are booked!',
        confirmedHint: 'Save the date — the host has been notified.',
      },
      settings: {
        title: 'Settings',
        description: 'Manage your account settings and preferences.',
        sections: {
          profile: 'Profile',
          notifications: 'Notifications',
          appearance: 'Appearance',
          security: 'Security',
          team: 'Team',
          projects: 'Projects',
          integrations: 'Integrations',
          issues: 'AI Issues',
          ai: 'AI',
        },
        issues: {
          title: 'AI Issues',
          description:
            'Review verify-generated issues and keep the list actionable by dismissing noise and reprioritizing.',
          project: 'Project',
          selectProject: 'Select a project',
          view: 'View',
          viewActive: 'Active',
          viewAll: 'All',
          noProject: 'Select a project to load issues.',
          empty: 'No issues found for this view.',
          updated: 'Updated',
          dismiss: 'Dismiss',
        },
        team: {
          title: 'Team Members',
          description: 'Manage your team and invite colleagues',
          invite: 'Invite',
          emailLabel: 'Email address',
          emailPlaceholder: 'colleague@company.com',
          roleLabel: 'Role',
          messageLabel: 'Personal message (optional)',
          messagePlaceholder: 'Hey! Join our team on RunRate...',
          sendInvite: 'Send Invitation',
          inviteSent: 'Invitation sent! Link copied to clipboard.',
          inviteResent: 'Invitation email sent',
          inviteError: 'Failed to send invitation',
          emailRequired: 'Email is required',
          noMembers: 'No team members yet. Invite your colleagues!',
          pendingInvitations: 'Pending Invitations',
          invitedBy: 'Invited by',
          copyLink: 'Copy invite link',
          resend: 'Resend invitation',
          cancelInvite: 'Cancel invitation',
          cancelInviteTitle: 'Cancel Invitation',
          cancelInviteDescription: 'Are you sure you want to cancel the invitation for {{email}}?',
          linkCopied: 'Invite link copied to clipboard',
          makeAdmin: 'Make Admin',
          makeMember: 'Make Member',
          remove: 'Remove from team',
          removeConfirmTitle: 'Remove Team Member',
          removeConfirmDescription: 'Are you sure you want to remove {{name}} from the team? They will lose access to all team resources.',
        },
        profile: {
          title: 'Profile Information',
          description: 'Update your personal information and profile photo.',
          firstName: 'First Name',
          lastName: 'Last Name',
          email: 'Email',
          role: 'Role',
          save: 'Save Changes',
          saving: 'Saving...',
          changePhoto: 'Change Photo',
          photoHint: 'JPG, GIF or PNG. Max size 2MB.',
        },
        notifications: {
          title: 'Notification Preferences',
          description: 'Manage how you receive notifications.',
          emailNotifications: 'Email Notifications',
          emailNotificationsDesc: 'Receive email notifications for important updates',
          browserNotifications: 'Push Notifications',
          browserNotificationsDesc: 'Receive browser push notifications',
          meetingReminders: 'Meeting Reminders',
          meetingRemindersDesc: 'Get notified before meetings start',
          followUpSuggestions: 'Follow-up Suggestions',
          followUpSuggestionsDesc: 'Receive suggestions for follow-up actions',
          dailyDigest: 'Daily Digest',
          dailyDigestDesc: 'Daily summary of your CRM activity',
        },
        appearance: {
          themeTitle: 'Theme',
          themeDescription: 'Choose how RunRate looks to you.',
          colorMode: 'Color Mode',
          light: 'Light',
          dark: 'Dark',
          system: 'System',
          languageTitle: 'Language & Region',
          languageDescription: 'Set your preferred language and timezone.',
          language: 'Language',
          english: 'English',
          spanish: 'Spanish',
          german: 'German',
          russian: 'Russian',
          timezone: 'Timezone',
          utc: 'UTC',
          est: 'Eastern Time',
          pst: 'Pacific Time',
          cet: 'Central European Time',
        },
        security: {
          passwordTitle: 'Password',
          passwordDescription: 'Change your password to keep your account secure.',
          currentPassword: 'Current Password',
          newPassword: 'New Password',
          confirmPassword: 'Confirm New Password',
          changePassword: 'Update Password',
          passwordSuccess: 'Password changed successfully',
          passwordError: 'Failed to change password',
          passwordMismatch: 'Passwords do not match',
          twoFactorTitle: 'Two-Factor Authentication',
          twoFactorDescription: 'Add an extra layer of security to your account.',
          enable2fa: 'Enable 2FA',
          twoFactorHint: 'Use an authenticator app for additional security',
          sessionsTitle: 'Sessions',
          sessionsDescription: 'Manage your active sessions across devices.',
          currentSession: 'Current',
          revoke: 'Revoke',
          noSessions: 'No active sessions found.',
        },
        projects: {
          title: 'Projects',
          description: 'Manage your projects and workspaces',
          newProject: 'New Project',
          name: 'Project Name',
          namePlaceholder: 'e.g. Marketing Campaign',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Brief project description',
          create: 'Create',
          noProjects: 'No projects yet',
          createFirst: 'Create your first project to get started',
          stats: {
            tasks: '{{count}} tasks',
            epics: '{{count}} epics',
            sprints: '{{count}} sprints',
          },
          archive: 'Archive',
          restore: 'Restore',
          delete: 'Delete',
          deleteConfirm: {
            title: 'Delete Project',
            description: 'Are you sure you want to permanently delete "{{name}}"? This action cannot be undone and all tasks, epics, and data will be lost.',
            confirm: 'Delete Project',
          },
          archived: 'Archived Projects',
          favorite: 'Favorite',
          manageChannels: 'Manage Channels',
          manageMembers: 'Manage Members',
          channels: {
            description: 'Manage communication channels for this project',
            assigned: 'Assigned Channels',
            add: 'Add Channel',
            selectToAdd: 'Select a channel to add to this project:',
            noAvailable: 'No more channels available to add',
            noAssigned: 'No channels assigned yet',
            global: 'Global Channels',
            autoIncluded: 'Auto-included',
            globalDescription: 'These channels are available in all projects',
            noGlobal: 'No global channels configured',
            helpTitle: 'How channels work',
            helpText: 'Assigned channels will filter inbox messages for this project. Global channels appear in all projects.',
          },
          members: {
            title: 'Project Members',
            description: 'Manage who can access this project',
            add: 'Add Member',
            confirmAdd: 'Add',
            selectUser: 'Select user',
            selectUserPlaceholder: 'Choose a team member',
            selectRole: 'Select role',
            empty: 'No members assigned yet',
            readOnlyHint: 'Only owners and admins can manage members.',
            remove: 'Remove member',
            errors: {
              load: 'Failed to load project members. Please try again.',
            },
            roles: {
              owner: 'Owner',
              admin: 'Admin',
              member: 'Member',
              viewer: 'Viewer',
            },
          },
        },
        integrations: {
          title: 'Connected Apps',
          description: 'Manage your connected applications and services.',
          connected: 'Connected',
          notConnected: 'Not connected',
          connect: 'Connect',
          disconnect: 'Disconnect',
          google: 'Gmail',
          googleDesc: 'Sync your Gmail inbox',
          microsoft: 'Microsoft 365',
          microsoftDesc: 'Sync Outlook email and calendar',
          whatsapp: 'WhatsApp',
          whatsappDesc: 'Connect WhatsApp Business',
          telegram: 'Telegram',
          telegramDesc: 'Connect Telegram bot',
          linkedin: 'LinkedIn',
          linkedinDesc: 'Connect LinkedIn messaging',
          hubspot: 'HubSpot',
          hubspotDesc: 'Sync contacts, companies, and deals with HubSpot CRM',
          syncNow: 'Sync Now',
          aimfox: 'AimFox',
          aimfoxDesc: 'LinkedIn outreach automation',
          configure: 'Configure',
          manageMessages: 'Open Messages',
          slackDesc: 'Get notifications in Slack',
          zoomDesc: 'Create meetings automatically',
        },
        aimfox: {
          title: 'AimFox Configuration',
          description: 'Connect your AimFox account for LinkedIn outreach automation.',
          apiKey: 'API Key',
          apiKeyPlaceholder: 'Enter your AimFox API key',
          apiKeySet: '••••••••••••',
          workspaceId: 'Workspace ID',
          workspaceIdPlaceholder: 'Enter workspace ID',
          automationTitle: 'Automation Settings',
          autoCreateContacts: 'Auto-create contacts from leads',
          autoCreateDeals: 'Auto-create deals on reply',
          syncLabels: 'Sync labels as tags',
          syncNotes: 'Sync notes to contacts',
          pauseOnWon: 'Pause campaign on deal won',
          removeOnLost: 'Remove from campaign on deal lost',
          testConnection: 'Test Connection',
          testSuccess: 'Connection successful',
          testFailed: 'Connection failed',
          campaigns: 'Campaigns',
          pauseCampaign: 'Pause',
          resumeCampaign: 'Resume',
        },
        ai: {
          keys: {
            title: 'AI API Keys',
            description: 'Connect your AI providers to enable model routing.',
            apiKey: 'API Key',
            baseUrl: 'Base URL',
            model: 'Model',
            selectModel: 'Select a model',
            recommended: 'recommended',
            recommendedModel: 'Recommended',
            configured: 'Configured',
            notConfigured: 'Not configured',
            save: 'Save Keys',
            remove: 'Remove',
            saveError: 'Failed to save keys. Please try again.',
            removeConfirm: {
              title: 'Remove API Key',
              description: 'Are you sure you want to remove the {{provider}} API key? You will need to re-enter it to use this provider again.',
              confirm: 'Remove Key',
            },
          },
          models: {
            title: 'Model Routing',
            description: 'Choose which model handles each AI task.',
            defaultModel: 'Default model',
            selectModel: 'Select a model',
            notConfigured: 'provider not configured',
            save: 'Save Routing',
            saving: 'Saving model routing...',
            saveError: 'Failed to save model routing. Please try again.',
          },
          providers: {
            kimi: {
              name: 'Kimi',
              keyPlaceholder: 'sk-...',
              baseUrlPlaceholder: 'https://api.moonshot.ai/v1',
            },
            openai: {
              name: 'OpenAI',
              keyPlaceholder: 'sk-...',
              baseUrlPlaceholder: 'https://api.openai.com/v1',
            },
            gemini: {
              name: 'Gemini',
              keyPlaceholder: 'AIza...',
              baseUrlPlaceholder: 'https://generativelanguage.googleapis.com',
            },
            anthropic: {
              name: 'Anthropic',
              keyPlaceholder: 'sk-ant-...',
              baseUrlPlaceholder: 'https://api.anthropic.com/v1',
            },
          },
          tasks: {
            entity_extraction: {
              title: 'Entity extraction',
              description: 'Extract structured entities from messages and notes.',
            },
            summarization: {
              title: 'Summarization',
              description: 'Summarize long conversations and threads.',
            },
            draft_generation: {
              title: 'Draft generation',
              description: 'Generate replies and drafts for communications.',
            },
            classification: {
              title: 'Classification',
              description: 'Classify intents and messages.',
            },
            meeting_preparation: {
              title: 'Meeting preparation',
              description: 'Prepare briefs before meetings.',
            },
            meeting_summary: {
              title: 'Meeting summary',
              description: 'Summarize meetings and extract action items.',
            },
            followup_detection: {
              title: 'Follow-up detection',
              description: 'Detect follow-ups and reminders.',
            },
            task_extraction: {
              title: 'Task extraction',
              description: 'Extract tasks from emails and conversations.',
            },
            smart_notifications: {
              title: 'Smart notifications',
              description: 'Generate intelligent notification summaries.',
            },
            style_adaptation: {
              title: 'Style adaptation',
              description: 'Adapt tone and writing style for replies.',
            },
            assistant_chat: {
              title: 'Assistant chat',
              description: 'Handle AI assistant chat requests.',
            },
            messaging_reply: {
              title: 'Messaging reply',
              description: 'Generate suggested replies for messaging.',
            },
            insight_generation: {
              title: 'Insight generation',
              description: 'Generate analytics insights and recommendations.',
            },
          },
          skills: {
            title: 'AI Skills Catalog',
            description: 'Enable or disable tools available to the assistant for this workspace.',
            empty: 'No skills available.',
            enabled: 'Enabled',
            disabled: 'Disabled',
            deprecated: 'Deprecated',
            uncategorized: 'Uncategorized',
            filters: {
              label: 'Filter',
              all: 'All categories',
              categoryPlaceholder: 'Category',
              searchLabel: 'Search skills',
              searchPlaceholder: 'Search skills...',
              empty: 'No skills match these filters.',
            },
            categories: {
              email: 'Email',
              pipeline: 'Pipeline',
              deal: 'Deals',
              contact: 'Contacts',
              interaction: 'Interactions',
              task: 'Tasks',
              onboarding: 'Onboarding',
              analysis: 'Analysis',
              meeting: 'Meeting',
              messaging: 'Messaging',
              tasks: 'Tasks',
              account: 'Account',
              communications: 'Communications',
              crm: 'CRM',
              sales: 'Sales',
              analytics: 'Analytics',
              automation: 'Automation',
              productivity: 'Productivity',
              ops: 'Operations',
              marketing: 'Marketing',
              support: 'Support',
              integrations: 'Integrations',
              system: 'System',
            },
            permissions: 'Permissions',
            cacheTtl: 'Cache TTL {{seconds}}s',
            version: 'v{{version}}',
            risk: {
              read_only: 'Read-only',
              write: 'Write',
              external_write: 'External write',
            },
            sensitivity: {
              public: 'Public',
              internal: 'Internal',
              confidential: 'Confidential',
              restricted: 'Restricted',
            },
          },
        },
      },
      actions: {
        title: 'Actions',
        subtitle: 'Manage your tasks and follow-ups',
        newAction: 'New Action',
        createAction: 'Create Action',
        filter: 'Filter',
        searchPlaceholder: 'Search actions...',
        loadingShort: '...',
        status: {
          todo: 'To Do',
          inProgress: 'In Progress',
          done: 'Done',
          dismissed: 'Dismissed',
          snoozed: 'Snoozed',
        },
        filters: {
          all: 'All',
        },
        priority: {
          high: 'High',
          medium: 'Medium',
          low: 'Low',
        },
        type: {
          follow_up: 'Follow Up',
          upsell: 'Upsell',
          at_risk: 'At Risk',
          support: 'Support',
          onboarding: 'Onboarding',
        },
        source: {
          email: 'Email',
          whatsapp: 'WhatsApp',
          telegram: 'Telegram',
          manual: 'Manual',
        },
        menu: {
          edit: 'Edit',
          markTodo: 'Mark as To Do',
          startProgress: 'Start Progress',
          markDone: 'Mark as Done',
          dismiss: 'Dismiss',
        },
        sheet: {
          createTitle: 'New Action',
          createDescription: 'Create a new action item',
          editTitle: 'Edit Action',
          editDescription: 'Update action details',
        },
        form: {
          title: 'Title',
          titlePlaceholder: 'What needs to be done?',
          description: 'Description',
          descriptionPlaceholder: 'Add details...',
          actionType: 'Type',
          source: 'Source',
          priority: 'Priority',
          status: 'Status',
          dueDate: 'Due Date',
        },
        empty: {
          title: 'No actions found',
          searchHint: 'Try adjusting your search or filters',
          cta: 'Create a new action to get started',
        },
        errors: {
          loadFailed: 'Failed to load actions. Please try again.',
        },
        labels: {
          assignee: 'Assignee',
        },
        badges: {
          ai: 'AI',
          aiWithActor: 'AI · {{actor}}',
          aiTooltip: 'Generated by Turia AI',
          aiTooltipNoActor: 'AI Generated',
        },
      },
      authVerify: {
        loading: {
          title: 'Verifying your login...',
          subtitle: 'Please wait while we verify your login link',
        },
        success: {
          title: 'Login Successful!',
          subtitle: 'You are now signed in. Redirecting...',
        },
        error: {
          title: 'Verification Failed',
          subtitle: 'Your login link may have expired. Please request a new one.',
          cta: 'Request New Login Link',
        },
        errors: {
          missingToken: 'Missing verification token',
          verificationFailed: 'Verification failed',
          unableToVerify: 'Unable to verify your login link. Please try again.',
        },
      },
      auth: {
        login: {
          welcome: 'Welcome to',
          brandPrimary: 'Run',
          brandSecondary: 'Rate',
          emailPlaceholder: 'Email address',
          submit: 'Log in or sign up',
          loading: 'Loading...',
          logoAlt: 'RunRate Logo',
          requestIdSuffix: ' (ref: {{id}})',
          messages: {
            checkEmail: 'Check your email for the login link!',
          },
          errors: {
            network: 'Network error. Please check your connection and try again.',
            tooManyAttempts: 'Too many attempts. Please try again later.',
            sendFailed: 'Failed to send magic link. Please try again.',
            httpStatus: ' (HTTP {{status}})',
          },
        },
      },
      footer: {
        copyright: '© {{year}} RunRate',
        privacy: 'Privacy & Terms of Use',
      },
      companies: {
        title: 'Companies',
        entity: 'Company',
        count: '{{count}} companies',
        filter: 'Filter',
        addCompany: 'Add Company',
        searchPlaceholder: 'Search companies...',
        actions: {
          viewDetails: 'View Details',
        },
        quickActions: {
          email: 'Email',
          emailTooltip: 'Send email to company',
          deal: 'Deal',
          dealTooltip: 'Create new deal',
        },
        table: {
          name: 'Name',
          industry: 'Industry',
          size: 'Size',
          location: 'Location',
          deals: 'Deals',
        },
        deals: {
          count: '{{count}} deals',
          totalValue: '{{value}} total',
        },
        empty: {
          title: 'No companies found',
          searchHint: 'Try adjusting your search or add a new company.',
          cta: 'Get started by adding your first company.',
        },
        errors: {
          loadFailed: 'Failed to load companies. Please try again.',
        },
      },
      contacts: {
        title: 'Contacts',
        entity: 'Contact',
        count: '{{count}} contacts',
        filter: 'Filter',
        addContact: 'Add Contact',
        searchPlaceholder: 'Search contacts...',
        table: {
          name: 'Name',
          contactInfo: 'Contact Info',
          tags: 'Tags',
          lastUpdated: 'Last Updated',
        },
        actions: {
          viewProfile: 'View Profile',
          sendEmail: 'Send Email',
          call: 'Call',
        },
        empty: {
          title: 'No contacts found',
          searchHint: 'Try adjusting your search or add a new contact.',
          cta: 'Get started by adding your first contact.',
        },
        errors: {
          loadFailed: 'Failed to load contacts. Please try again.',
        },
        duplicates: {
          title: 'Potential Duplicates',
          badge: '{{count}} duplicates',
          found: 'Found {{count}} potential duplicate groups',
          none: 'No duplicates found',
          allClean: 'All contacts are unique!',
          merge: 'Merge',
          reason: {
            email: 'Same email',
            name: 'Same name',
            phone: 'Same phone',
          },
        },
        time: {
          minutesAgo: '{{count}} minutes ago',
          hoursAgo: '{{count}} hours ago',
          daysAgo: '{{count}} days ago',
          weeksAgo: '{{count}} weeks ago',
        },
      },
      contactDetail: {
        actions: {
          backToRecords: 'Back to Records',
          backToContacts: 'Back to Contacts',
        },
        errors: {
          notFound: 'Contact not found',
          notFoundDescription: 'The contact you are looking for does not exist.',
        },
        tabs: {
          timeline: 'Timeline',
          emails: 'Emails',
          tasks: 'Tasks',
          deals: 'Deals',
          notes: 'Notes',
          linkedin: 'LinkedIn',
        },
        insights: {
          title: 'Turia Insights',
          next: 'Next:',
        },
        linkedin: {
          empty: {
            title: 'No LinkedIn data',
            description: 'Connect AimFox to see LinkedIn engagement data for this contact.',
          },
          status: {
            connected: 'Connected',
            pending: 'Pending',
            declined: 'Declined',
          },
          viewProfile: 'View Profile',
          messagesSent: 'Messages Sent',
          messagesReceived: 'Messages Received',
          responseRate: 'Response Rate',
          campaigns: 'Campaigns',
          lastMessage: 'Last message',
        },
        timeline: {
          title: 'Activity Timeline',
          addNote: 'Add Note',
          errors: {
            loadFailed: 'Failed to load activity. Please try again.',
          },
          empty: {
            title: 'No activity yet',
            description: 'Activity will appear here as you interact with this contact',
          },
        },
        emails: {
          errors: {
            loadFailed: 'Failed to load emails. Please try again.',
          },
          empty: {
            title: 'No emails yet',
            description: 'Emails will appear here once synced',
          },
        },
        files: {
          title: 'Files',
          open: 'Open',
          errors: {
            loadFailed: 'Failed to load files. Please try again.',
          },
        },
        tasks: {
          errors: {
            loadFailed: 'Failed to load tasks. Please try again.',
          },
          empty: {
            title: 'No tasks yet',
            description: 'Create a task to track follow-ups',
          },
        },
        deals: {
          errors: {
            loadFailed: 'Failed to load deals. Please try again.',
          },
          noCloseDate: 'No close date',
          empty: {
            title: 'No deals yet',
            description: 'Track sales opportunities here',
          },
        },
        notes: {
          errors: {
            loadFailed: 'Failed to load notes. Please try again.',
          },
          empty: {
            title: 'No notes yet',
            description: 'Add notes to remember important details',
          },
        },
      },
      messages: {
        title: 'Messages',
        searchPlaceholder: 'Search conversations...',
        newMessage: 'New Message',
        send: 'Send',
        inputPlaceholder: 'Type a message...',
        conversationActive: 'Conversation active',
        noMessagesYet: 'No messages yet',
        conversationFallback: 'Conversation {{id}}',
        accounts: 'Accounts',
        activeAccountLabel: 'Inbox account',
        accountStatus: {
          connected: 'Connected',
          notConnected: 'Not connected',
        },
        empty: {
          noAccountsTitle: 'No messaging accounts connected',
          noAccountsDescription: 'Connect WhatsApp or Telegram to start messaging',
          noConversationsTitle: 'No conversations yet',
          noConversationsDescription: 'Start a conversation with your contacts',
          noMessagesInConversation: 'No messages yet in this conversation.',
          selectTitle: 'Select a conversation',
          selectDescription: 'Choose a conversation from the list or start a new one to begin messaging',
        },
        errors: {
          loadConversations: 'Failed to load conversations. Please try again.',
          loadMessages: 'Failed to load messages. Please try again.',
        },
        telegramAuth: {
          title: 'Connect Telegram account',
          subtitle: 'Use your personal Telegram account to sync conversations.',
          connect: 'Connect',
          manage: 'Manage',
          sendCode: 'Send code',
          resendCode: 'Resend code',
          verifyCode: 'Verify code',
          submitPassword: 'Submit password',
          phoneLabel: 'Phone number',
          phonePlaceholder: '+34 600 000 000',
          displayNameLabel: 'Display name',
          displayNamePlaceholder: 'Telegram Account',
          codeLabel: 'Login code',
          codePlaceholder: '12345',
          passwordLabel: 'Two-factor password',
          passwordPlaceholder: 'Your Telegram password',
          connectedNotice: 'Telegram account connected.',
          defaultDisplayName: 'Telegram Account',
          status: {
            connected: 'Connected',
            notConnected: 'Not connected',
            codeSent: 'Code sent',
            passwordRequired: 'Password required',
            error: 'Connection error',
          },
          errors: {
            phoneRequired: 'Phone number is required',
            codeRequired: 'Code is required',
            passwordRequired: 'Password is required',
            accountMissing: 'Telegram account not found',
            generic: 'Unable to connect Telegram',
          },
        },
        whatsappAuth: {
          title: 'Connect WhatsApp account',
          subtitle: 'Connect WhatsApp Cloud API or WhatsApp Web for messaging.',
          connect: 'Connect',
          manage: 'Manage',
          createAndConnect: 'Create & Connect',
          defaultDisplayName: 'WhatsApp Account',
          accountLabel: 'WhatsApp account',
          accountNew: 'New account',
          connectionTypeLabel: 'Connection type',
          connectionTypes: {
            cloud: 'WhatsApp Cloud API',
            onPrem: 'WhatsApp Web',
          },
          displayNameLabel: 'Display name',
          displayNamePlaceholder: 'WhatsApp Account',
          locationLabel: 'Location ID',
          locationPlaceholder: 'Location UUID',
          locationCustom: 'Custom location ID',
          phoneLabel: 'Phone number (optional)',
          phonePlaceholder: '+34 600 000 000',
          cloud: {
            title: 'Cloud credentials',
            phoneNumberIdLabel: 'Phone number ID',
            phoneNumberIdPlaceholder: '1234567890',
            businessIdLabel: 'Business account ID (optional)',
            businessIdPlaceholder: '987654321',
            accessTokenLabel: 'Access token',
            accessTokenPlaceholder: 'EAAG...',
            apiVersionLabel: 'API version',
            apiVersionPlaceholder: 'v19.0',
            verifyTokenLabel: 'Verify token (optional)',
            verifyTokenPlaceholder: 'auto-generated if empty',
          },
          qrTitle: 'QR code',
          qrHint: 'Connect to generate a QR code for WhatsApp Web.',
          qrExpires: 'Expires at {{time}}',
          status: {
            connected: 'Connected',
            notConnected: 'Not connected',
          },
          actions: {
            connect: 'Connect',
            reconnect: 'Reconnect',
            refreshQr: 'Refresh QR',
            disconnect: 'Disconnect',
          },
          errors: {
            locationRequired: 'Location ID is required',
            phoneNumberIdRequired: 'Phone number ID is required',
            accessTokenRequired: 'Access token is required',
            accountMissing: 'WhatsApp account not found',
            generic: 'Unable to connect WhatsApp',
          },
        },
      },
      supportPipeline: {
        title: 'Support Pipeline',
        subtitle: 'Manage customer support tickets',
        newTicket: 'New Ticket',
        createTicket: 'Create Ticket',
        filter: 'Filter',
        retry: 'Retry',
        searchPlaceholder: 'Search tickets...',
        status: {
          new: 'New',
          inProgress: 'In Progress',
          waitingCustomer: 'Waiting Customer',
          waitingInternal: 'Waiting Internal',
          resolved: 'Resolved',
          closed: 'Closed',
        },
        priority: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          urgent: 'Urgent',
        },
        columns: {
          new: 'New',
          inProgress: 'In Progress',
          waitingCustomer: 'Waiting on Customer',
          waitingInternal: 'Waiting internal',
          resolved: 'Resolved',
        },
        labels: {
          unknownRequester: 'Unknown',
        },
        empty: {
          column: 'No tickets',
          title: 'No support tickets',
          description: 'When customers submit support requests, they will appear here',
        },
        errors: {
          loadFailed: 'Failed to load tickets. Please try again.',
          unknown: 'Unknown error',
        },
        stats: {
          open: 'Open',
          inProgress: 'In Progress',
          waitingCustomer: 'Waiting',
          slaBreached: 'SLA Breached',
          avgResponse: 'Avg Response',
          total: 'Total',
        },
        filters: {
          allStatuses: 'All Statuses',
          allPriorities: 'All Priorities',
          allCategories: 'All Categories',
          allAssignees: 'All Assignees',
          assignedToMe: 'Assigned to Me',
        },
        table: {
          ticket: 'Ticket',
          status: 'Status',
          priority: 'Priority',
          category: 'Category',
          assignee: 'Assignee',
          source: 'Source',
          created: 'Created',
          assigned: 'Assigned',
          unassigned: 'Unassigned',
        },
        pagination: {
          page: 'Page {{page}}',
          pageOf: 'Page {{page}} of {{total}}',
          previous: 'Previous',
          next: 'Next',
        },
        view: {
          board: 'Board',
          table: 'Table',
        },
        loadingTickets: 'Loading tickets...',
      },
      oauthCallback: {
        providers: {
          google: 'Google',
          microsoft: 'Microsoft',
          email: 'email',
        },
        loading: {
          title: 'Setting up your email...',
          subtitle: 'Configuring {{provider}} notifications',
        },
        success: {
          title: 'Email Connected!',
          subtitle: 'Your {{provider}} account has been successfully connected.',
        },
        warning: {
          title: 'Partially Connected',
          subtitle: 'Your {{provider}} account is connected, but email notifications could not be enabled.',
        },
        error: {
          title: 'Connection Failed',
        },
        actions: {
          continueToInbox: 'Continue to Inbox',
          retryNotifications: 'Retry Notification Setup',
          continueAnyway: 'Continue Anyway',
          tryAgain: 'Try Again',
          skip: 'Skip for now',
        },
        errors: {
          authFailed: 'OAuth authorization failed',
          invalidState: 'Invalid callback state',
          generic: 'Unable to connect your email account. Please try again.',
          watchNotSupported: 'Notifications are not supported for {{provider}}.',
        },
        watchFailed: 'Failed to setup email notifications',
      },
      oauth: {
        connecting: 'Connecting...',
        success: 'Successfully connected!',
        error: 'Connection failed',
        warning: 'Partially connected',
        retry: 'Retry',
        goToInbox: 'Go to Inbox',
      },
      header: {
        projectScope: {
          all: 'All projects',
          manage: 'Manage projects',
        },
        themeToggle: 'Toggle theme',
        user: {
          settings: 'Settings',
          logout: 'Log out',
        },
      },
      commandPalette: {
        trigger: 'Search',
        placeholder: 'Search commands...',
        input: 'Search commands...',
        empty: 'No results found.',
        loading: 'Loading...',
        noContacts: 'No contacts found',
        noCompanies: 'No companies found',
        noProjects: 'No projects found',
        group: {
          recent: 'Recent',
          contacts: 'Contacts',
          companies: 'Companies',
          projects: 'Projects',
          scope: 'Project scope',
        },
        scope: {
          all: 'All projects',
        },
      },
      invite: {
        loading: 'Loading invitation...',
        title: 'You\'re Invited!',
        subtitle: '{{name}} has invited you to join their team',
        joiningOrg: 'You\'ll be joining',
        invitedAs: 'Invited as',
        accept: 'Accept Invitation',
        notExpected: 'Didn\'t expect this?',
        ignoreInvite: 'Ignore this invite',
        securityNote: 'This invitation link is unique and can only be used once.',
        success: 'Welcome to the Team!',
        successDescription: 'Your invitation has been accepted successfully.',
        redirecting: 'Redirecting you to the dashboard...',
        invalid: 'Invalid Invitation',
        invalidDescription: 'This invitation link is invalid or has already been used.',
        goToLogin: 'Go to Login',
        expired: 'Invitation Expired',
        expiredDescription: 'This invitation has expired. Please contact the team admin for a new invitation.',
        contactAdmin: 'Ask your administrator to send a new invitation.',
        acceptError: 'Failed to accept invitation. Please try again.',
      },
      common: {
        back: 'Back',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        create: 'Create',
        add: 'Add',
        comingSoon: 'Coming soon',
        unavailable: 'Unavailable',
        settingsLinks: {
          ai: 'Open Settings → AI',
          integrations: 'Open Settings → Integrations',
        },
      },
      blocks: {
        blockedBy: 'Blocked by',
        blocks: 'Blocks',
        blocked: 'Blocked',
        noBlocks: 'This task doesn\'t block any other tasks',
        addBlock: 'Add blocking task',
        searchTasks: 'Search tasks...',
        noTasksFound: 'No tasks found',
        blockedWarning: 'This task cannot be started until the blocking tasks are completed',
        removeBlock: 'Remove block',
      },
      estimates: {
        label: 'Estimate',
        points: 'points',
        select: 'Set estimate...',
        clear: 'Clear estimate',
        effort: {
          trivial: 'Trivial - quick fix',
          small: 'Small - few hours',
          medium: 'Medium - half a day',
          large: 'Large - full day',
          xlarge: 'X-Large - multiple days',
          epic: 'Epic - needs breakdown',
        },
      },
      cycles: {
        label: 'Cycle',
        none: 'No cycle',
        select: 'Select cycle...',
        search: 'Search cycles...',
        empty: 'No cycles found',
        create: 'Create Cycle',
        edit: 'Edit Cycle',
        createDescription: 'Create a new cycle to organize your work into sprints.',
        editDescription: 'Update cycle details and settings.',
        deleteTitle: 'Delete Cycle',
        deleteDescription: 'Are you sure you want to delete "{{name}}"? Tasks in this cycle will be unassigned.',
        duration: '{{days}} days',
        progress: 'Progress',
        tasks: 'tasks',
        points: 'points',
        active: 'Active',
        planned: 'Planned',
        completed: 'Completed',
        status: {
          planned: 'Planned',
          active: 'Active',
          completed: 'Completed',
          cancelled: 'Cancelled',
        },
        fields: {
          name: 'Name',
          namePlaceholder: 'Sprint 1, Q1 2026...',
          goal: 'Goal',
          goalPlaceholder: 'What do you want to achieve in this cycle?',
          startDate: 'Start Date',
          endDate: 'End Date',
          status: 'Status',
          description: 'Description',
          descriptionPlaceholder: 'Additional notes about this cycle...',
        },
      },
      confirmSheet: {
        subtitle: 'Please confirm this action',
      },
      deleteConfirmSheet: {
        title: 'Delete {{entity}}',
        subtitle: 'This action cannot be undone.',
        description:
          'Are you sure you want to delete "{{name}}"? This will permanently remove the {{entity}} and all associated data.',
      },
      actionCard: {
        types: {
          followUp: 'Follow Up',
          upsell: 'Upsell Opportunity',
          atRisk: 'At Risk',
          support: 'Support',
          onboarding: 'Onboarding',
        },
        reasoningPrefix: '💡',
        dueLabel: 'Due:',
        actions: {
          complete: 'Complete',
          snooze: 'Snooze',
          dismiss: 'Dismiss',
        },
      },
      insightCard: {
        types: {
          skepticConversion: 'Skeptic Conversion',
          silentComplaint: 'Silent Complaint',
          salesCoaching: 'Sales Coaching',
          competitiveIntel: 'Competitive Intel',
        },
        recommendedActions: 'Recommended Actions:',
        more: '+{{count}} more...',
        confidence: 'Confidence:',
        viewDetails: 'View Details',
      },
      mainLayout: {
        search: 'Search',
        workspace: {
          personal: 'Personal',
        },
        projects: {
          allProjects: 'All Projects',
          noProjects: 'No projects yet',
        },
        sidebar: {
          collapse: 'Collapse',
          expand: 'Expand sidebar',
        },
        nav: {
          home: 'Home',
          actions: 'Actions',
          inbox: 'Inbox',
          calendar: 'Calendar',
          booking: 'Booking',
          kanban: 'Kanban',
          tasks: 'Tasks',
          pages: 'Pages',
          analytics: 'Analytics',
          pipelines: 'Pipelines',
          sales: 'Sales',
          support: 'Support',
          records: 'Records',
          contacts: 'Contacts',
          companies: 'Companies',
          saasAdmin: 'SaaS Admin',
          settings: 'Settings',
        },
        theme: {
          light: 'Light mode',
          dark: 'Dark mode',
        },
        user: {
          logout: 'Log out',
        },
      },
      errorBoundary: {
        title: 'Something went wrong',
        subtitle: "We're sorry for the inconvenience. The application encountered an unexpected error.",
        devDetails: 'Error Details (Development Only)',
        componentStack: 'Component Stack:',
        actions: {
          tryAgain: 'Try Again',
          reload: 'Reload Page',
        },
        supportHint: 'If this problem persists, please contact support.',
      },
      progressTimeline: {
        completed: 'Completed',
        days: '{{count}}d',
      },
      pagination: {
        ariaLabel: 'Pagination',
        previous: 'Previous page',
        next: 'Next page',
        page: 'Page {{page}}',
        info: 'Showing {{start}} to {{end}} of {{total}} results',
      },
      project: {
        onboarding: {
          title: 'New Project',
          nameRequired: 'Please enter a project name',
          errorFailed: 'Failed to create project. Please try again.',
          createProject: 'Create Project',
          steps: {
            details: 'Details',
            channels: 'Channels',
            finish: 'Finish',
          },
          step1: {
            title: 'Project Details',
            description: 'Give your project a name and customize its appearance.',
            nameLabel: 'Project Name',
            namePlaceholder: 'e.g., RunDesk, Marketing Campaign',
            descriptionLabel: 'Description (optional)',
            descriptionPlaceholder: 'Brief project description',
            colorLabel: 'Color',
            iconLabel: 'Icon',
            preview: 'Preview',
            untitled: 'Untitled Project',
            noDescription: 'No description',
          },
          step2: {
            title: 'Connect Channels',
            description: 'Select which communication channels should be linked to this project.',
            email: 'Email',
            noEmail: 'No email accounts connected yet',
            connectNewEmail: 'Connect a new email account',
            global: 'Available in all projects',
            specific: 'Project-specific',
            tip: 'You can add more channels later in Project Settings',
          },
          step3: {
            title: 'Ready to Launch',
            description: 'Review your project settings and create it.',
            connectedChannels: 'Connected channels:',
            ready: 'Everything looks good! Click "Create Project" to get started.',
          },
        },
      },
      editor: {
        placeholder: 'Start writing...',
      },
      analytics: {
        dashboardNamePlaceholder: 'My Dashboard',
      },
      forecast: {
        title: 'Team Forecast',
        period: {
          week: 'This Week',
          month: 'This Month',
          quarter: 'This Quarter',
        },
        metrics: {
          committed: 'Committed',
          committedDesc: 'High probability (>75%)',
          bestCase: 'Best Case',
          bestCaseDesc: 'Medium probability (>50%)',
          pipeline: 'Pipeline',
          pipelineDesc: 'Weighted value',
          predicted: 'AI Predicted',
          confidence: 'confidence',
        },
        repBreakdown: 'Rep Breakdown',
        repBreakdownDesc: 'Individual performance and forecast by sales rep',
        noReps: 'No team members found for this pipeline',
        table: {
          rep: 'Rep',
          committed: 'Committed',
          bestCase: 'Best Case',
          pipeline: 'Pipeline',
          deals: 'Deals',
          quota: 'Quota Progress',
          trend: 'Trend',
        },
        openDeals: 'open deals',
        tooltip: {
          openValue: 'Open Value',
          weightedValue: 'Weighted',
          expectedToClose: 'Expected to close this period',
        },
        teamTotal: 'Team Total',
        confidence: 'Confidence',
        periodLabel: 'Period',
        vsLastPeriod: 'vs last period',
        quotaAttainment: 'Quota attainment',
        quotaTarget: 'of {{value}} target',
        error: {
          title: 'Failed to load forecast',
          generic: 'An unexpected error occurred',
        },
      },
      widget: {
        selectDataSource: 'Select a data source',
      },
      relatedItems: {
        title: 'Related Context',
        currentItem: 'Current Item',
        noSubject: '(No subject)',
        unknownPage: 'Page',
        error: 'Failed to load related items',
        empty: 'No related items found',
        emptyHint: 'Add more context to see AI suggestions',
        trigger: 'AI Context',
        generatedAt: 'AI analysis from {{time}}',
        resolvedAt: 'Resolved {{time}}',
        citation: {
          source: 'Source',
          basedOn: 'Based on',
        },
        sections: {
          actions: 'Suggested Actions',
          emails: 'Related Emails',
          pages: 'Related Docs',
          similar: 'Similar Closed Cases',
        },
      },
      projectHub: {
        error: 'Failed to load project hub',
        pulse: {
          title: 'AI Pulse',
          description: 'AI-generated insights and recommendations',
          tasksOverdue: 'tasks overdue',
          tasksBlocked: 'tasks blocked',
          summaryTemplate: '{{completionRate}}% tasks completed. {{activeCount}} tasks in progress. {{blockedCount}} blocked.',
          nextActions: 'Recommended Next Actions',
          basedOn: 'Based on',
          sources: 'Based on:',
          updatedAt: 'Updated {{time}}',
        },
        stats: {
          title: 'Quick Stats',
          inProgress: 'In Progress',
          done: 'Done',
          blocked: 'Blocked',
          overdue: 'Overdue',
        },
        execution: {
          title: 'Execution',
          progress: 'Sprint Progress',
          blockers: 'Blockers',
        },
        crm: {
          title: 'Business Context',
          activeDeals: 'Active Deals',
          stakeholders: 'Key Stakeholders',
        },
        knowledge: {
          title: 'Knowledge',
          pinned: 'Pinned',
          recent: 'Recently Edited',
          topSnippets: 'Top KB Snippets',
        },
      },
    },
  },
  ru: {
    translation: {
      app: {
        brandName: 'RunRate',
      },
      onboarding: {
        brandName: 'RunRate',
        steps: {
          createWorkspace: 'Создать проект',
          connectEmail: 'Подключить почту',
          selectEmail: 'Выбрать почту',
          connect: 'Подключить',
          manualEmailSetup: 'Ручная настройка почты',
        },
        descriptions: {
          createWorkspace: 'Создайте первый проект',
          connectEmail: 'Свяжите почтового провайдера',
          manualEmailSetup: 'Настройте почту',
        },
        workspace: {
          title: 'Создать проект',
          description: 'Создайте первый проект для отслеживания сделок и задач.',
          nameLabel: 'Название проекта',
          namePlaceholder: 'напр., Рост выручки Q1',
          domainLabel: 'Домен компании',
          domainPlaceholder: 'company.com',
          domainHint: 'Пользователи с этим почтовым доменом смогут автоматически присоединиться',
          domainFallback: 'домен',
          allowTeammates: 'Разрешить коллегам присоединяться с этим доменом',
          allowTeammatesWithDomain: 'Разрешить коллегам присоединяться автоматически с адресами @{{domain}}',
          errorEmpty: 'Введите название проекта и домен, чтобы продолжить.',
          errorFailed: 'Не удалось создать проект. Попробуйте ещё раз.',
        },
        email: {
          title: 'Подключить почту',
          description: 'Синхронизируйте входящие, чтобы отслеживать переписку автоматически.',
          providerLabel: 'Почтовый провайдер',
          googleWorkspace: 'Google Workspace',
          microsoft365: 'Microsoft 365',
          otherProvider: 'Другой провайдер',
          skip: 'Пропустить',
          errorFailed: 'Не удалось подключиться. Попробуйте ещё раз.',
        },
        // IMAP/SMTP section removed — no backend implementation exists
        buttons: {
          back: 'Назад',
          continue: 'Продолжить',
          connect: 'Подключить',
          skip: 'Пропустить',
          connectAndComplete: 'Подключить и завершить',
          connectAndFinish: 'Подключить и завершить',
          goToSettings: 'Перейти в настройки',
          saving: 'Сохраняем...',
          finishSetup: 'Завершить настройку',
        },
        messaging: {
          title: 'Добавьте каналы сообщений',
          description: 'Подключите WhatsApp и Telegram для единого инбокса.',
          whatsappTitle: 'WhatsApp Business',
          whatsappDescription: 'Официальное API‑подключение',
          telegramTitle: 'Telegram Bot',
          telegramDescription: 'Подключите вашего бота',
          stepLabel: 'Мессенджеры',
          whatsappStep: 'WhatsApp',
          telegramStep: 'Telegram',
          errorFailed: 'Не удалось сохранить настройки мессенджеров. Попробуйте ещё раз.',
        },
        connect: {
          title: 'Подключить и завершить',
          description: 'Авторизуйте почтового провайдера, чтобы начать синхронизацию.',
          providerTitle: 'Подключить {{provider}}',
          providerDescription: 'Мы синхронизируем почту и календарь, чтобы всё было в одном месте. Сейчас вы будете перенаправлены для авторизации доступа.',
        },
      },
      dashboard: {
        greeting: {
          morning: 'Доброе утро',
          afternoon: 'Добрый день',
          evening: 'Добрый вечер',
          subtitle: 'Вот что происходит сегодня',
        },
        stats: {
          openTasks: 'Открытые задачи',
          pipelineValue: 'Воронка продаж',
          openDeals: 'открытых сделок',
          contacts: 'Контакты',
          companies: 'Компании',
          wonDeals: 'Выиграно в этом месяце',
          dealsWon: 'сделок выиграно',
          monthlyTarget: 'Месячная цель',
        },
        ai: {
          title: 'AI Ассистент',
          placeholder: 'Спросите Turia о чём угодно...',
          heroPlaceholder: 'Спросите AI о чём угодно...',
          shortcut: 'Cmd+Enter',
          skillsLabel: 'Навыки',
          fastMode: 'Быстрый режим',
          configure: 'Настроить AI',
          send: 'Отправить',
          skills: {
            draftEmail: 'Черновик письма',
            summarize: 'Суммировать контент',
            createTask: 'Создать задачу',
            quickReply: 'Быстрый ответ',
            research: 'Исследовать тему',
          },
          prompts: {
            draftEmail: 'Составь письмо о: ',
            summarize: 'Кратко перескажи: ',
            createTask: 'Создай задачу: ',
            quickReply: 'Сгенерируй быстрый ответ: ',
            research: 'Исследуй и расскажи про: ',
          },
        },
        metrics: {
          contacts: 'Контакты',
          tasks: 'Открытые задачи',
          deals: 'Сделки',
          revenue: 'Выручка',
          actions: {
            contacts: 'Открыть контакты',
            tasks: 'Открыть задачи',
            deals: 'Открыть сделки',
            revenue: 'Открыть выручку',
          },
        },
        briefing: {
          title: 'Ежедневный брифинг',
          empty: 'Пока нет брифинга.',
          actions: {
            prepare: 'Подготовиться к синку',
            viewProposal: 'Посмотреть предложение',
          },
        },
        activity: {
          title: 'Недавняя активность',
          viewAll: 'Смотреть все',
          empty: 'Нет недавней активности.',
          justNow: 'Только что',
        },
        schedule: {
          title: 'Расписание на сегодня',
          open: 'Открыть календарь',
          empty: 'Сегодня нет событий.',
          connectHint: 'Подключите календарь, чтобы видеть события.',
        },
        tasks: {
          title: 'Приоритетные задачи',
          viewAll: 'Смотреть все',
          empty: 'Нет приоритетных задач',
          add: 'Добавить задачу',
          priorityNormal: 'Обычная',
        },
        actions: {
          title: 'Действия',
          completed: 'Выполненные',
          viewAll: 'Смотреть все',
          empty: 'Пока нет действий. Попросите Turia AI создать их.',
        },
        calendar: {
          yesterday: 'Вчера',
          empty: 'Сегодня нет встреч',
          viewAll: 'Открыть календарь',
        },
      },
      inbox: {
        title: 'Входящие',
        search: 'Поиск писем...',
        searchPlaceholder: 'Поиск...',
        noEmails: 'Писем не найдено',
        noMessagesFound: 'Сообщений не найдено',
        markAsRead: 'Отметить как прочитанное',
        markAsUnread: 'Отметить как непрочитанное',
        refresh: 'Обновить',
        summaryGenerating: 'Генерируем сводку...',
        summaryFailed: 'Не удалось создать сводку',
        aiNotConfigured: 'ИИ не настроен для этого воркспейса. Добавьте ключ Kimi в Настройки → AI.',
        aiEmptyThread: 'В этой переписке пока нет текста для анализа.',
        aiFailed: 'Не удалось сгенерировать. Попробуйте ещё раз.',
        noMessages: 'Сообщений пока нет.',
        messageUnavailable: 'Содержимое сообщения недоступно.',
        noContent: 'Нет содержимого',
        aiSummary: {
          label: 'Сводка AI',
        },
        message: {
          unknownSender: 'Неизвестно',
          noSubject: '(Без темы)',
        },
        tags: {
          important: 'Важно',
        },
        folders: {
          inbox: 'Входящие',
          sent: 'Отправленные',
          archive: 'Архив',
          trash: 'Корзина',
        },
        channels: {
          all: 'Все каналы',
          email: 'Почта',
          whatsapp: 'WhatsApp',
          telegram: 'Telegram',
          linkedin: 'LinkedIn',
        },
        actions: {
          turiaInsights: 'Turia Insights',
          createTask: 'Создать задачу',
          addToCalendar: 'Добавить в календарь',
          archive: 'Архивировать',
          delete: 'Удалить',
          reply: 'Ответить',
          forward: 'Переслать',
          viewProfile: 'Профиль',
        },
        deleteConfirm: {
          title: 'Удалить навсегда',
          description: 'Вы уверены, что хотите навсегда удалить "{{subject}}"? Это действие нельзя отменить.',
          thisItem: 'этот элемент',
          confirm: 'Удалить навсегда',
        },
        summary: {
          title: 'Сводка переписки',
          createTask: 'Создать задачу',
          draftReply: 'Черновик ответа',
        },
        reply: {
          title: 'Ответ',
          saved: 'Черновик сохранён',
          placeholder: 'Введите ответ...',
          drafting: 'Turia готовит черновик...',
          draftWithTuria: 'Черновик с Turia',
          send: 'Отправить',
          sendFailed: 'Не удалось отправить сообщение. Попробуйте ещё раз.',
          quotePrefix: 'Оригинальное сообщение:',
          templates: {
            replyPrefix: 'Спасибо за обновление.',
            forwardHeader: 'Пересланное сообщение',
            forwardFrom: 'От: {{name}} <{{email}}>',
            forwardSubject: 'Тема: {{subject}}',
            forwardDate: 'Дата: {{date}}',
          },
        },
        tasks: {
          defaultTitle: 'Фоллоу‑ап: {{subject}}',
        },
        attachments: {
          title: 'Вложения',
          placeholder: 'attachment.pdf',
        },
        contactProfile: {
          title: 'Профиль контакта',
          viewProfile: 'Открыть профиль',
          addToCrm: 'Добавить в CRM',
          relatedTasks: 'Связанные задачи',
          noTasks: 'Нет задач для этого контакта',
          history: 'История',
          noHistory: 'История недоступна',
        },
        emptyState: {
          title: 'Выберите переписку',
          description: 'Выберите тред из списка, чтобы посмотреть детали и ответить.',
        },
        errors: {
          noThreadSelected: 'Тред не выбран',
        },
      },
      calendar: {
        header: {
          today: 'Сегодня',
          addEvent: 'Добавить событие',
          view: {
            day: 'День',
            week: 'Неделя',
            month: 'Месяц',
          },
        },
        connectHint: 'Подключите календарь, чтобы видеть события.',
        sidebar: {
          myCalendars: 'Мои календари',
          meetings: 'Встречи',
          tasksDeadlines: 'Задачи и дедлайны',
          holidays: 'Праздники',
          team: 'Команда',
        },
        briefing: {
          title: 'Ежедневный брифинг',
          button: 'Turia Briefing',
          empty: 'День свободен. Добавьте события или задачи, чтобы получить инсайты от Turia.',
        },
        team: {
          empty: 'Нет данных по команде.',
        },
        event: {
          create: 'Новое событие',
          edit: 'Редактировать событие',
          details: 'Детали события',
          dialogDescription: 'Создание и просмотр деталей события календаря.',
          openOriginal: 'Открыть в оригинальном календаре',
          titlePlaceholder: 'Добавить заголовок',
          dateTime: 'Дата и время',
          allDay: 'Весь день',
          start: 'Начало',
          end: 'Конец',
          locationPlaceholder: 'Добавить локацию',
          noLocation: 'Без локации',
          description: 'Описание',
          descriptionPlaceholder: 'Добавить описание',
          noDescription: 'Без описания',
          guests: 'Гости',
          addGuestPlaceholder: 'Добавить email гостя',
          defaultReminders: 'Напоминания календаря по умолчанию',
          customReminders: 'Пользовательские напоминания',
          noReminders: 'Без напоминаний',
          visibility: {
            default: 'Видимость по умолчанию',
            public: 'Публично',
            private: 'Приватно',
          },
          showAs: 'Показывать как',
        },
      },
      kanban: {
        header: {
          workItems: 'Рабочие элементы',
        },
        columns: {
          backlog: 'Бэклог',
          todo: 'К выполнению',
          in_progress: 'В работе',
          done: 'Готово',
          cancelled: 'Отменено',
        },
        priority: {
          low: 'Низкий',
          medium: 'Средний',
          high: 'Высокий',
          urgent: 'Срочно',
        },
        actions: {
          edit: 'Редактировать',
          delete: 'Удалить',
          addTask: 'Добавить задачу',
          addWorkItem: 'Добавить элемент',
          display: 'Вид',
          analytics: 'Аналитика',
        },
        view: {
          board: 'Доска',
          list: 'Список',
          gantt: 'Таймлайн',
        },
        empty: {
          noTasks: 'Нет задач',
          dropHere: 'Перетащите задачи сюда или создайте новую',
        },
        gantt: {
          taskLabel: 'Задача',
          zoom: 'Масштаб',
          day: 'День',
          week: 'Неделя',
          month: 'Месяц',
        },
        labels: {
          unassigned: 'Не назначено',
        },
        bulk: {
          selected: '{{count}} выбрано',
          clear: 'Снять выбор',
          statusPlaceholder: 'Сменить статус',
          assigneePlaceholder: 'Назначить',
          apply: 'Применить',
          delete: 'Удалить',
          deleteTitle: 'Удалить задачи?',
          deleteDescription: 'Будут удалены задачи: {{count}}.',
          deleteConfirm: 'Удалить',
          unassigned: 'Не назначено',
          selectTask: 'Выбрать задачу {{id}}',
          selectGroup: 'Выбрать задачи {{status}}',
        },
        badges: {
          bmcp: 'ИИ',
          bmcpActor: 'ИИ · {{actor}}',
          bmcpTitle: 'Создано через BMCP',
          bmcpTitleActor: 'Создано {{actor}} через BMCP',
        },
        entities: {
          task: 'Задача',
        },
      },
      pages: {
        title: 'Страницы',
        search: 'Поиск страниц...',
        favorites: 'Избранное',
        allPages: 'Все страницы',
        untitled: 'Без названия',
        unsaved: 'Несохранённые изменения',
        locked: 'Заблокировано',
        titlePlaceholder: 'Без названия',
        contentPlaceholder: 'Начните писать... Используйте @ чтобы упомянуть людей, задачи или страницы',
        removeIcon: 'Убрать иконку',
        savedAt: 'Сохранено в {{time}}',
        createdBy: 'Создано: {{name}}',
        lastUpdated: 'Обновлено {{date}}',
        empty: {
          title: 'Пока нет страниц',
          subtitle: 'Создайте первую страницу, чтобы начать',
        },
        actions: {
          create: 'Новая страница',
          edit: 'Редактировать',
          duplicate: 'Дублировать',
          share: 'Поделиться',
          importMarkdown: 'Импорт .md',
          favorite: 'В избранное',
          unfavorite: 'Убрать из избранного',
          lock: 'Заблокировать',
          unlock: 'Разблокировать',
          archive: 'Архивировать',
        },
        toolbar: {
          bold: 'Жирный',
          italic: 'Курсив',
          strikethrough: 'Зачёркнутый',
          h1: 'Заголовок 1',
          h2: 'Заголовок 2',
          h3: 'Заголовок 3',
          bulletList: 'Маркированный список',
          orderedList: 'Нумерованный список',
          taskList: 'Список задач',
          quote: 'Цитата',
          code: 'Блок кода',
          link: 'Ссылка',
          image: 'Изображение',
        },
      },
      deals: {
        header: {
          defaultTitle: 'Воронка продаж',
          pipelineValue: '{{value}} в воронке',
        },
        views: {
          kanban: 'Канбан',
          timeline: 'Таймлайн',
          calendar: 'Календарь',
          coverage: 'Покрытие',
        },
        actions: {
          editDeal: 'Редактировать сделку',
          viewDetails: 'Посмотреть детали',
          delete: 'Удалить',
          addDeal: 'Добавить сделку',
          newDeal: 'Новая сделка',
          markWon: 'Отметить как выигранную',
          markLost: 'Отметить как проигранную',
          followUp: 'Фоллоу-ап',
          followUpTitle: 'Фоллоу-ап: {{title}}',
        },
        labels: {
          companyId: 'Компания #{{id}}',
          owner: 'Владелец',
          noDate: 'Нет даты',
          totalValue: '{{value}} всего',
          weightedValue: 'Взвешенно: {{value}}',
          daysSinceActivity: '{{count}} дн. с последней активности',
          overdue: 'Просрочено',
          aiScore: 'ИИ {{score}}',
          saving: 'Сохраняем...',
        },
        badges: {
          followUp: 'Фоллоу-ап',
        },
        filters: {
          pipeline: 'Воронка',
          minValue: 'Мин. сумма',
          maxValue: 'Макс. сумма',
          tagsPlaceholder: 'Теги (через запятую)',
          owner: 'Владелец',
          ownerAll: 'Все владельцы',
          ownerSearch: 'Поиск владельца...',
          ownerEmpty: 'Владельцы не найдены',
          sortBy: 'Сортировка',
          sortOrder: 'Порядок',
          reset: 'Сбросить',
          sort: {
            position: 'Позиция',
            value: 'Сумма',
            lastActivity: 'Последняя активность',
            createdAt: 'Дата создания',
          },
          order: {
            asc: 'По возрастанию',
            desc: 'По убыванию',
          },
        },
        errors: {
          loadPipeline: 'Не удалось загрузить воронку. Попробуйте ещё раз.',
          moveFailed: 'Не удалось переместить сделку. Изменения откатились.',
        },
        timeline: {
          thisMonth: 'Этот месяц',
          invalidRange: 'Некорректный диапазон дат',
          empty: 'Нет сделок в этом диапазоне',
          dealCount: '{{count}} сделок',
          owner: 'Владелец',
        },
        calendar: {
          closing: 'Закрываются',
          followUps: 'Фоллоу-апы',
          emptyDay: 'Нет активности',
        },
        coverage: {
          title: 'Покрытие воронки',
          subtitle: 'Покрытие воронки относительно квоты',
          empty: 'Нет данных о покрытии',
          quota: 'Цель (квота)',
          current: 'Текущая воронка',
          weighted: 'Взвешенная воронка',
          ratio: 'Коэффициент покрытия',
          gap: 'Разрыв до 3×',
          status: {
            healthy: 'Норма',
            at_risk: 'Риск',
            critical: 'Критично',
          },
        },
        alerts: {
          title: 'Риски по сделкам',
          loading: 'Загружаем уведомления...',
          error: 'Не удалось загрузить уведомления',
          dismiss: 'Скрыть',
          empty: 'Нет уведомлений для выбранных фильтров.',
          filters: {
            label: 'Фильтр уведомлений',
            all: 'Все уведомления',
          },
          summary: {
            critical: '{{count}} критических',
            warning: '{{count}} предупреждений',
            info: '{{count}} инфо',
          },
          severity: {
            critical: 'Критично',
            warning: 'Предупреждение',
            info: 'Инфо',
          },
        },
        entities: {
          deal: 'Сделка',
        },
      },
      customerInsights: {
        header: {
          title: 'Инсайты о клиентах',
          subtitle: 'ИИ‑подсказки, которые помогают лучше понимать и вовлекать клиентов',
        },
        summary: {
          total: 'Всего',
          critical: 'Критично',
          warning: 'Предупреждения',
          info: 'Инфо',
          dismissed: 'Скрытые',
        },
        filters: {
          typeLabel: 'Тип:',
          severityLabel: 'Серьёзность:',
          allTypes: 'Все типы',
          type: {
            skeptic_conversion: 'Сомневающиеся',
            silent_complaint: 'Скрытые жалобы',
            sales_coaching: 'Коучинг продаж',
            competitive_intel: 'Конкурентная аналитика',
          },
          severity: {
            all: 'Все',
            critical: 'Критично',
            warning: 'Предупреждение',
            info: 'Инфо',
          },
        },
        tabs: {
          all: 'Все ({{count}})',
          critical: 'Критично ({{count}})',
          warning: 'Предупреждения ({{count}})',
          info: 'Инфо ({{count}})',
        },
        empty: {
          title: 'Инсайтов нет',
          subtitle: 'Попробуйте изменить фильтры или зайдите позже',
        },
        emptyBySeverity: {
          critical: 'Нет критичных инсайтов',
          warning: 'Нет предупреждений',
          info: 'Нет инфо‑инсайтов',
        },
        errors: {
          loadingTitle: 'Ошибка загрузки инсайтов',
          loadingMessage: 'Попробуйте ещё раз.',
        },
        details: {
          description: 'Описание',
          recommendedActions: 'Рекомендуемые действия',
          confidence: 'Уверенность',
          severity: 'Серьёзность',
          category: 'Категория',
          created: 'Создано',
        },
      },
      contextGraph: {
        header: {
          title: 'Контекстный граф',
          subtitle: 'Исследуйте связи и отношения в ваших данных',
        },
        actions: {
          resetView: 'Сбросить вид',
          populate: 'Заполнить граф',
        },
        stats: {
          nodes: 'Узлы:',
          edges: 'Связи:',
          types: 'Типы:',
          loading: '...',
        },
        empty: {
          title: 'Нет данных графа',
          subtitle: 'Нажмите «Заполнить граф», чтобы создать узлы из данных CRM',
        },
        pathFinder: {
          title: 'Найти путь',
          sourceLabel: 'Исходный узел',
          sourcePlaceholder: 'Выберите узел или вставьте ID',
          targetLabel: 'Целевой узел',
          targetPlaceholder: 'Введите ID целевого узла',
          findPath: 'Найти путь',
          pathFound: 'Путь найден (узлов: {{nodes}}, переходов: {{hops}})',
          noPath: 'Путь не найден',
        },
        selectedNode: {
          title: 'Выбранный узел',
          exploreNeighbors: 'Показать соседей',
        },
        legend: {
          title: 'Типы узлов',
          instructions: {
            click: 'Клик: выбрать узел',
            doubleClick: 'Двойной клик: соседи',
            drag: 'Перетаскивание: перемещать узлы',
            scroll: 'Скролл: зум',
          },
        },
        populateResult: {
          title: 'Граф заполнен!',
          message: '{{nodes}} узлов, {{edges}} связей',
        },
        nodeTypes: {
          contact: 'Контакт',
          deal: 'Сделка',
          company: 'Компания',
          email: 'Письмо',
          ticket: 'Тикет',
          event: 'Событие',
          decision: 'Решение',
        },
      },
      dailyActions: {
        header: {
          title: 'Ежедневные действия',
          subtitle: 'ИИ‑приоритеты на сегодня • всего {{count}}',
        },
        actions: {
          refresh: 'Обновить',
          generate: 'Сгенерировать действия',
          generateNew: 'Сгенерировать новые действия',
        },
        filters: {
          label: 'Фильтр:',
          types: {
            all: 'Все действия',
            at_risk: 'В зоне риска',
            follow_up: 'Фоллоу‑ап',
            upsell: 'Апселл',
            support: 'Поддержка',
            onboarding: 'Онбординг',
          },
        },
        tabs: {
          all: 'Все ({{count}})',
          high: 'Высокий приоритет ({{count}})',
          medium: 'Средний приоритет ({{count}})',
          low: 'Низкий приоритет ({{count}})',
        },
        empty: {
          title: 'Все задачи закрыты!',
          subtitle: 'Сейчас нет ожидающих действий.',
        },
        emptyByPriority: {
          high: 'Нет действий с высоким приоритетом',
          medium: 'Нет действий со средним приоритетом',
          low: 'Нет действий с низким приоритетом',
        },
        errors: {
          loadingTitle: 'Ошибка загрузки действий',
        },
        snooze: {
          title: 'Отложить действие',
          until: 'Отложить до',
          action: 'Отложить',
        },
      },
      emailDraftEditor: {
        header: {
          title: 'Редактор черновика письма',
          subtitle: 'Редактируйте и отправляйте черновики писем, созданные ИИ',
          aiGenerated: 'Сгенерировано ИИ',
        },
        alerts: {
          sent: 'Письмо отправлено!',
          missingRecipient: 'Введите email получателя',
        },
        errors: {
          loadingTitle: 'Ошибка загрузки черновика',
          notFound: 'Черновик не найден',
        },
        fields: {
          recipient: 'Email получателя',
          subject: 'Тема',
          body: 'Текст письма',
          tone: 'Тон',
          contextType: 'Тип контекста',
          created: 'Создано',
          sent: 'Отправлено',
        },
        placeholders: {
          recipient: 'recipient@example.com',
          subject: 'Тема письма',
          body: 'Текст письма',
        },
        actions: {
          copy: 'Копировать',
          copied: 'Скопировано',
          send: 'Отправить письмо',
          save: 'Сохранить черновик',
          regenerate: 'Пересоздать',
        },
        sidebar: {
          settingsTitle: 'Настройки черновика',
          contextTitle: 'Контекст генерации',
        },
        tone: {
          professional: 'Профессиональный',
          friendly: 'Дружелюбный',
          formal: 'Официальный',
          casual: 'Неформальный',
        },
        contextType: {
          manual: 'Вручную',
          thread: 'Переписка',
          contact: 'Контакт',
          deal: 'Сделка',
          ticket: 'Тикет',
        },
      },
      contactDetailSheet: {
        title: 'Обзор контакта',
        description: 'Детали контакта, инсайты, хронология и файлы.',
        actions: {
          openFullPage: 'Открыть профиль',
        },
        errors: {
          load: 'Не удалось загрузить детали контакта.',
        },
        insights: {
          title: 'Инсайты Turia',
          empty: 'Инсайтов пока нет. Подключите коммуникации для расширения контекста.',
          highlights: 'Хайлайты',
          nextActions: 'Следующие действия',
        },
        timeline: {
          title: 'Хронология',
          empty: 'Пока нет активности.',
        },
        files: {
          title: 'Файлы',
          empty: 'Файлы не найдены.',
          fileFallback: 'Файл',
          open: 'Открыть',
        },
      },
      onboardingProgress: {
        header: {
          title: 'Онбординг клиента',
          subtitle: 'Отслеживайте и управляйте прогрессом онбординга',
        },
        status: {
          active: 'Активно',
          paused: 'Пауза',
          completed: 'Завершено',
          failed: 'Ошибка',
        },
        progress: {
          title: 'Общий прогресс',
          step: 'Шаг {{current}} из {{total}}',
        },
        currentStep: {
          label: 'Текущий шаг',
        },
        actions: {
          label: 'Действия:',
          nextStep: 'Следующий шаг',
          pause: 'Пауза',
          resume: 'Продолжить',
          skip: 'Пропустить шаг',
          cancel: 'Отменить процесс',
        },
        complete: {
          title: 'Онбординг завершён!',
          subtitle: 'Клиент успешно прошёл все шаги онбординга',
        },
        timeline: {
          title: 'Таймлайн процесса',
        },
        confirm: {
          cancelTitle: 'Отменить процесс',
          skipTitle: 'Пропустить шаг',
          cancelDescription:
            'Вы уверены, что хотите отменить этот процесс онбординга? Действие нельзя отменить.',
          skipDescription:
            'Вы уверены, что хотите пропустить текущий шаг? Процесс перейдёт к следующему шагу.',
        },
        errors: {
          loadingTitle: 'Ошибка загрузки процесса онбординга',
          notFound: 'Процесс не найден',
          missingIds: 'Не указан ID клиента или процесса',
        },
      },
      saasAdminSupport: {
        header: {
          title: 'Центр поддержки',
          subtitle: 'Управляйте тикетами и FAQ',
        },
        actions: {
          manageFaq: 'Управлять FAQ',
          refresh: 'Обновить',
        },
        stats: {
          open: 'Открытые',
          inProgress: 'В работе',
          waitingCustomer: 'Ожидание клиента',
          slaBreached: 'SLA нарушен',
          avgResponse: 'Среднее время ответа',
          total: 'Всего',
        },
        filters: {
          searchLabel: 'Поиск',
          searchPlaceholder: 'Искать тикеты...',
          statusLabel: 'Статус',
          priorityLabel: 'Приоритет',
          categoryLabel: 'Категория',
          assigneeLabel: 'Исполнитель',
          allStatuses: 'Все статусы',
          allPriorities: 'Все приоритеты',
          allCategories: 'Все категории',
          allAssignees: 'Все исполнители',
          assignedToMe: 'Назначено мне',
        },
        status: {
          open: 'Открыт',
          in_progress: 'В работе',
          waiting_customer: 'Ожидание клиента',
          waiting_internal: 'Ожидание команды',
          resolved: 'Решён',
          closed: 'Закрыт',
        },
        priority: {
          urgent: 'Срочно',
          high: 'Высокий',
          medium: 'Средний',
          low: 'Низкий',
        },
        table: {
          ticket: 'Тикет',
          status: 'Статус',
          priority: 'Приоритет',
          category: 'Категория',
          assignee: 'Исполнитель',
          source: 'Источник',
          created: 'Создан',
          assigned: 'Назначен',
          unassigned: 'Не назначен',
        },
        source: {
          email: 'Почта',
          chat: 'Чат',
          web: 'Веб',
          api: 'API',
        },
        loadingTickets: 'Загрузка тикетов...',
        empty: {
          title: 'Тикеты не найдены',
          subtitle: 'Попробуйте изменить фильтры',
        },
        fallbacks: {
          empty: '—',
        },
        pagination: {
          page: 'Страница {{page}}',
          pageOf: 'Страница {{page}} из {{total}}',
          previous: 'Назад',
          next: 'Далее',
        },
        errors: {
          loadTickets: 'Не удалось загрузить тикеты. Попробуйте ещё раз.',
        },
      },
      saasAdminSupportDetail: {
        header: {
          title: 'Детали тикета',
          subtitle: 'Просмотрите переписку и обновите статус.',
        },
        actions: {
          backToSupport: 'Назад к поддержке',
          assignToMe: 'Назначить мне',
          saveChanges: 'Сохранить изменения',
        },
        labels: {
          ticket: 'Тикет',
          id: 'ID: {{id}}',
          created: 'Создан {{date}}',
          category: 'Категория: {{category}}',
        },
        fields: {
          status: 'Статус',
          priority: 'Приоритет',
          category: 'Категория',
          assignee: 'Исполнитель',
        },
        placeholders: {
          status: 'Выберите статус',
          priority: 'Выберите приоритет',
          category: 'Выберите категорию',
        },
        fallbacks: {
          uncategorized: 'Без категории',
        },
        conversation: {
          title: 'Переписка',
          loading: 'Загрузка…',
          count: '{{count}} сообщений',
          empty: 'Сообщений пока нет.',
          internalSuffix: ' · внутреннее',
        },
        reply: {
          title: 'Ответ',
          subtitle: 'Отправьте ответ клиенту.',
          messageLabel: 'Сообщение',
          internalNote: 'Внутреннее примечание (не видно клиенту)',
          send: 'Отправить',
        },
        alerts: {
          ticketUpdated: 'Тикет обновлён.',
          assignedToYou: 'Назначено на вас.',
          messageSent: 'Сообщение отправлено.',
        },
        errors: {
          missingTicketId: 'Отсутствует ticketId',
          notAuthenticated: 'Не авторизован',
          ticketNotLoaded: 'Тикет не загружен',
          updateTicket: 'Не удалось обновить тикет',
          assignTicket: 'Не удалось назначить тикет',
          sendMessage: 'Не удалось отправить сообщение',
          missingMessage: 'Нужно заполнить сообщение.',
          ticketNotFound: 'Тикет не найден.',
        },
        loadingTicket: 'Загрузка тикета...',
      },
      companySheet: {
        title: {
          edit: 'Редактировать компанию',
          new: 'Новая компания',
        },
        description: {
          edit: 'Обновите информацию о компании ниже.',
          new: 'Добавьте новую компанию в CRM.',
        },
        fields: {
          name: 'Название компании',
          domain: 'Домен',
          industry: 'Отрасль',
          size: 'Размер компании',
          location: 'Локация',
          tags: 'Теги',
        },
        placeholders: {
          name: 'Acme Inc.',
          domain: 'acme.com',
          industry: 'Выберите отрасль',
          size: 'Выберите размер',
          location: 'Сан-Франциско, CA',
          tags: 'Добавить тег',
        },
        options: {
          notSpecified: 'Не указано',
          size: '{{size}} сотрудников',
          industry: {
            technology: 'Технологии',
            healthcare: 'Здравоохранение',
            finance: 'Финансы',
            education: 'Образование',
            retail: 'Ритейл',
            manufacturing: 'Производство',
            real_estate: 'Недвижимость',
            consulting: 'Консалтинг',
            marketing: 'Маркетинг',
            other: 'Другое',
          },
        },
        actions: {
          addTag: 'Добавить',
          save: 'Сохранить изменения',
          create: 'Создать компанию',
        },
      },
      companyDetailSheet: {
        title: 'Детали компании',
        errors: {
          load: 'Не удалось загрузить данные компании.',
        },
        sections: {
          overview: 'Обзор',
        },
        fields: {
          domain: 'Домен',
          industry: 'Отрасль',
          size: 'Размер компании',
          location: 'Локация',
        },
        fallbacks: {
          notProvided: 'Не указано',
          noTags: 'Теги не добавлены.',
        },
      },
      contactSheet: {
        title: {
          edit: 'Редактировать контакт',
          new: 'Новый контакт',
        },
        description: {
          edit: 'Обновите информацию о контакте ниже.',
          new: 'Добавьте новый контакт в CRM.',
        },
        fields: {
          name: 'Имя',
          email: 'Email',
          phone: 'Телефон',
          company: 'Компания',
          tags: 'Теги',
        },
        placeholders: {
          name: 'Иван Иванов',
          email: 'ivan@example.com',
          phone: '+7 (999) 123-45-67',
          company: 'Выберите компанию',
          tags: 'Добавить тег',
        },
        options: {
          noCompany: 'Без компании',
        },
        actions: {
          addTag: 'Добавить',
          save: 'Сохранить изменения',
          create: 'Создать контакт',
        },
      },
      dealSheet: {
        title: {
          edit: 'Редактировать сделку',
          new: 'Новая сделка',
        },
        description: {
          edit: 'Обновите информацию о сделке ниже.',
          new: 'Создайте новую сделку в воронке.',
        },
        fields: {
          title: 'Название сделки',
          value: 'Сумма сделки',
          currency: 'Валюта',
          pipeline: 'Воронка',
          stage: 'Стадия',
          contact: 'Контакт',
          company: 'Компания',
          expectedClose: 'Ожидаемое закрытие',
        },
        placeholders: {
          title: 'Лицензия на корпоративное ПО',
          value: '10000',
          pipeline: 'Выберите воронку',
          stage: 'Выберите стадию',
          contact: 'Выберите контакт',
          company: 'Выберите компанию',
        },
        options: {
          noContact: 'Без контакта',
          noCompany: 'Без компании',
        },
        actions: {
          save: 'Сохранить изменения',
          create: 'Создать сделку',
        },
        score: {
          title: 'AI-оценка сделки',
          loading: 'Считаем оценку...',
          confidence: 'Уверенность',
          lastCalculated: 'Последний расчёт: {{date}}',
          labels: {
            engagement: 'Вовлечённость',
            fit: 'Соответствие',
            intent: 'Интент',
            similarity: 'Сходство',
          },
        },
        nextAction: {
          title: 'Следующее лучшее действие',
          loading: 'Генерируем рекомендацию...',
          empty: 'Пока нет рекомендаций.',
          confidence: 'Уверенность',
          reasoning: 'Обоснование',
          suggestedContent: 'Черновик ответа',
          copyDraft: 'Скопировать',
          copied: 'Скопировано',
          priority: {
            high: 'Высокий',
            medium: 'Средний',
            low: 'Низкий',
          },
          actions: {
            send_followup: 'Отправить фоллоу‑ап',
            schedule_demo: 'Назначить демо',
            send_proposal: 'Отправить предложение',
            introduce_champion: 'Вовлечь чемпиона',
            share_case_study: 'Поделиться кейсом',
            address_objection: 'Снять возражение',
            offer_discount: 'Предложить скидку',
          },
        },
      },
      taskSheet: {
        title: {
          edit: 'Редактировать задачу',
          new: 'Новая задача',
        },
        description: {
          edit: 'Обновите информацию о задаче ниже.',
          new: 'Создайте новую задачу для трекинга работы.',
        },
        fields: {
          title: 'Название',
          description: 'Описание',
          status: 'Статус',
          state: 'Состояние',
          priority: 'Приоритет',
          dueDate: 'Срок',
          epic: 'Эпик',
          sprint: 'Спринт',
          labels: 'Метки',
          watchers: 'Наблюдатели',
          subtasks: 'Подзадачи',
          timeEntries: 'Учёт времени',
          relationships: 'Связанные задачи',
          blocks: 'Зависимости',
        },
        placeholders: {
          title: 'Сделать фоллоу‑ап клиенту',
          description: 'Добавьте подробности о задаче...',
        },
        status: {
          backlog: 'Бэклог',
          todo: 'К выполнению',
          inProgress: 'В работе',
          done: 'Готово',
          cancelled: 'Отменено',
        },
        priority: {
          low: 'Низкий',
          medium: 'Средний',
          high: 'Высокий',
          urgent: 'Срочно',
        },
        actions: {
          save: 'Сохранить изменения',
          create: 'Создать задачу',
        },
        tabs: {
          details: 'Детали',
          comments: 'Комментарии',
          links: 'Ссылки',
          attachments: 'Файлы',
          activity: 'Активность',
        },
        templates: {
          hint: 'Начните с шаблона или создайте задачу с нуля',
          skipLink: 'Пропустить и создать пустую задачу',
        },
      },
      taskDetail: {
        comments: {
          empty: 'Пока нет комментариев. Будьте первым!',
          placeholder: 'Напишите комментарий...',
        },
        links: {
          empty: 'Пока нет прикреплённых ссылок.',
          add: 'Добавить ссылку',
          urlPlaceholder: 'https://...',
        },
        attachments: {
          empty: 'Файлов пока нет.',
          add: 'Добавить файл',
          filePath: 'Ссылка на файл',
          filePathPlaceholder: 'https://files.example.com/report.pdf',
          fileName: 'Имя файла',
          fileNamePlaceholder: 'report.pdf',
          fileSize: 'Размер файла',
          fileSizePlaceholder: 'Размер в МБ',
          mimeType: 'MIME‑тип',
          mimeTypePlaceholder: 'application/pdf',
          description: 'Описание',
          descriptionPlaceholder: 'Дополнительные заметки о файле',
        },
        watchers: {
          title: 'Наблюдатели',
          count: '{{count}} наблюдают',
          watch: 'Следить за задачей',
          unwatch: 'Перестать следить',
          empty: 'Пока нет наблюдателей.',
          unknownUser: 'Неизвестный пользователь',
          notificationLabel: 'Уведомления',
          expirationLabel: 'Срок',
          notifications: {
            all: 'Все обновления',
            mentions: 'Только упоминания',
            status_only: 'Только смена статуса',
          },
          expiration: {
            never: 'Никогда',
            oneDay: '1 день',
            sevenDays: '7 дней',
            thirtyDays: '30 дней',
            custom: 'Произвольно',
          },
          expiresAt: 'Истекает {{date}}',
        },
        timeEntries: {
          total: '{{hours}} ч',
          add: 'Добавить время',
          empty: 'Пока нет записей времени.',
          hoursPlaceholder: 'Часы (например, 1.5)',
          loggedAtPlaceholder: 'Дата и время',
          notePlaceholder: 'Что было сделано?',
          entryTitle: '{{hours}} ч',
        },
        activity: {
          empty: 'Пока нет записей активности.',
        },
        subtasks: {
          add: 'Добавить подзадачу',
          placeholder: 'Что нужно сделать?',
          progress: '{{done}} из {{total}} выполнено',
        },
        labels: {
          add: 'Добавить метку',
          placeholder: 'Введите название метки',
          empty: 'Нет меток',
        },
        relationships: {
          empty: 'Нет связанных задач',
          add: 'Добавить связь',
          typePlaceholder: 'Выберите тип',
          targetPlaceholder: 'ID целевой задачи',
          descriptionPlaceholder: 'Описание (необязательно)',
          types: {
            blocks: 'Блокирует',
            blocked_by: 'Заблокировано',
            relates_to: 'Связано с',
            duplicates: 'Дублирует',
            duplicated_by: 'Дублируется',
            parent_of: 'Родитель для',
            child_of: 'Дочерняя от',
          },
        },
      },
      ticketSheet: {
        title: {
          edit: 'Редактировать тикет',
          new: 'Новый тикет',
        },
        description: {
          edit: 'Обновите информацию о тикете поддержки.',
          new: 'Создайте новый тикет поддержки.',
        },
        fields: {
          subject: 'Тема',
          description: 'Описание',
          status: 'Статус',
          priority: 'Приоритет',
          requesterEmail: 'Email заявителя',
        },
        placeholders: {
          subject: 'Краткое описание проблемы',
          description: 'Добавьте подробности о проблеме...',
          requesterEmail: 'customer@example.com',
        },
        actions: {
          save: 'Сохранить изменения',
          create: 'Создать тикет',
        },
      },
      booking: {
        title: 'Онлайн-запись',
        subtitle: 'Клиенты бронируют время сами — поделитесь ссылкой, RunRate сделает остальное.',
        min: 'мин',
        tabs: { eventTypes: 'Типы встреч', availability: 'Доступность', bookings: 'Брони' },
        weekdays: { sun: 'Вс', mon: 'Пн', tue: 'Вт', wed: 'Ср', thu: 'Чт', fri: 'Пт', sat: 'Сб' },
        status: { confirmed: 'Подтверждена', pending: 'Ожидает', cancelled: 'Отменена', rescheduled: 'Перенесена', no_show: 'Неявка' },
        eventTypes: {
          hint: 'Шаблоны встреч, которые могут бронировать гости.',
          new: 'Новый тип встречи',
          titleLabel: 'Название',
          titlePlaceholder: 'Знакомство, 30 минут',
          slugLabel: 'Слаг',
          durationLabel: 'Длительность (мин)',
          created: 'Тип встречи создан',
          empty: 'Пока нет типов встреч. Создайте первый и поделитесь ссылкой.',
          inactive: 'Выключен',
          copyLink: 'Копировать ссылку',
          linkCopied: 'Скопировано!',
          deleteTitle: 'Удалить тип встречи?',
          deleteDescription: '«{{title}}» перестанет принимать новые брони. Существующие брони сохранятся.',
        },
        availability: {
          hint: 'Профили рабочих часов для ваших типов встреч.',
          newDefault: 'Добавить рабочие часы',
          defaultName: 'Рабочие часы',
          created: 'Расписание создано',
          empty: 'Доступность не настроена. Добавьте рабочие часы.',
          default: 'По умолчанию',
        },
        bookings: { empty: 'Броней пока нет.' },
      },
      publicBooking: {
        notFound: 'Эта страница записи не существует или отключена.',
        pickSlot: 'Выберите время',
        prevWeek: 'Предыдущая неделя',
        nextWeek: 'Следующая неделя',
        noSlots: 'На этой неделе свободных слотов нет — посмотрите следующую.',
        yourDetails: 'Ваши данные',
        name: 'Имя',
        email: 'Email',
        confirm: 'Подтвердить запись',
        bookError: 'Слот только что заняли или произошла ошибка. Выберите другое время.',
        confirmedTitle: 'Вы записаны!',
        confirmedHint: 'Сохраните дату — организатор уже уведомлён.',
      },
      settings: {
        title: 'Настройки',
        description: 'Управляйте настройками аккаунта и предпочтениями.',
        sections: {
          profile: 'Профиль',
          notifications: 'Уведомления',
          appearance: 'Внешний вид',
          security: 'Безопасность',
          team: 'Команда',
          projects: 'Проекты',
          integrations: 'Интеграции',
          issues: 'AI проблемы',
          ai: 'ИИ',
        },
        issues: {
          title: 'AI проблемы',
          description:
            'Просматривайте проблемы из verify и поддерживайте список в рабочем состоянии: dismiss шум и меняйте приоритет.',
          project: 'Проект',
          selectProject: 'Выберите проект',
          view: 'Фильтр',
          viewActive: 'Активные',
          viewAll: 'Все',
          noProject: 'Выберите проект, чтобы загрузить проблемы.',
          empty: 'Проблем не найдено для этого фильтра.',
          updated: 'Обновлено',
          dismiss: 'Скрыть',
        },
        team: {
          title: 'Участники команды',
          description: 'Управляйте командой и приглашайте коллег',
          invite: 'Пригласить',
          emailLabel: 'Email адрес',
          emailPlaceholder: 'colleague@company.com',
          roleLabel: 'Роль',
          messageLabel: 'Личное сообщение (опционально)',
          messagePlaceholder: 'Привет! Присоединяйся к нашей команде в RunRate...',
          sendInvite: 'Отправить приглашение',
          inviteSent: 'Приглашение отправлено! Ссылка скопирована.',
          inviteResent: 'Письмо с приглашением отправлено',
          inviteError: 'Не удалось отправить приглашение',
          emailRequired: 'Email обязателен',
          noMembers: 'Пока нет участников. Пригласите коллег!',
          pendingInvitations: 'Ожидающие приглашения',
          invitedBy: 'Пригласил',
          copyLink: 'Скопировать ссылку',
          resend: 'Отправить повторно',
          cancelInvite: 'Отменить приглашение',
          cancelInviteTitle: 'Отмена приглашения',
          cancelInviteDescription: 'Вы уверены, что хотите отменить приглашение для {{email}}?',
          linkCopied: 'Ссылка скопирована в буфер обмена',
          makeAdmin: 'Сделать админом',
          makeMember: 'Сделать участником',
          remove: 'Удалить из команды',
          removeConfirmTitle: 'Удаление участника',
          removeConfirmDescription: 'Вы уверены, что хотите удалить {{name}} из команды? Он потеряет доступ ко всем ресурсам команды.',
        },
        profile: {
          title: 'Информация профиля',
          description: 'Обновите личные данные и фото профиля.',
          firstName: 'Имя',
          lastName: 'Фамилия',
          email: 'Email',
          role: 'Роль',
          save: 'Сохранить изменения',
          saving: 'Сохраняем...',
          changePhoto: 'Сменить фото',
          photoHint: 'JPG, GIF или PNG. Макс. 2 МБ.',
        },
        notifications: {
          title: 'Настройки уведомлений',
          description: 'Выберите, как вы хотите получать уведомления.',
          emailNotifications: 'Email‑уведомления',
          emailNotificationsDesc: 'Получать email‑уведомления о важных событиях',
          browserNotifications: 'Push‑уведомления',
          browserNotificationsDesc: 'Получать push‑уведомления в браузере',
          meetingReminders: 'Напоминания о встречах',
          meetingRemindersDesc: 'Получать напоминания перед встречами',
          followUpSuggestions: 'Подсказки для фоллоу‑апа',
          followUpSuggestionsDesc: 'Получать подсказки для последующих действий',
          dailyDigest: 'Ежедневный дайджест',
          dailyDigestDesc: 'Ежедневное резюме активности в CRM',
        },
        appearance: {
          themeTitle: 'Тема',
          themeDescription: 'Выберите, как выглядит RunRate.',
          colorMode: 'Цветовая тема',
          light: 'Светлая',
          dark: 'Тёмная',
          system: 'Системная',
          languageTitle: 'Язык и регион',
          languageDescription: 'Выберите язык и часовой пояс.',
          language: 'Язык',
          english: 'Английский',
          spanish: 'Испанский',
          german: 'Немецкий',
          russian: 'Русский',
          timezone: 'Часовой пояс',
          utc: 'UTC',
          est: 'Восточное время',
          pst: 'Тихоокеанское время',
          cet: 'Центрально‑европейское время',
        },
        security: {
          passwordTitle: 'Пароль',
          passwordDescription: 'Смените пароль, чтобы защитить аккаунт.',
          currentPassword: 'Текущий пароль',
          newPassword: 'Новый пароль',
          confirmPassword: 'Подтвердите новый пароль',
          changePassword: 'Обновить пароль',
          passwordSuccess: 'Пароль успешно изменён',
          passwordError: 'Не удалось изменить пароль',
          passwordMismatch: 'Пароли не совпадают',
          twoFactorTitle: 'Двухфакторная аутентификация',
          twoFactorDescription: 'Добавьте дополнительный уровень защиты для аккаунта.',
          enable2fa: 'Включить 2FA',
          twoFactorHint: 'Используйте приложение‑аутентификатор для дополнительной защиты',
          sessionsTitle: 'Сессии',
          sessionsDescription: 'Управляйте активными сессиями на устройствах.',
          currentSession: 'Текущая',
          revoke: 'Отозвать',
          noSessions: 'Нет активных сессий.',
        },
        projects: {
          title: 'Проекты',
          description: 'Управляйте проектами и рабочими пространствами',
          newProject: 'Новый проект',
          name: 'Название проекта',
          namePlaceholder: 'например, Маркетинговая кампания',
          descriptionLabel: 'Описание',
          descriptionPlaceholder: 'Краткое описание проекта',
          create: 'Создать',
          noProjects: 'Пока нет проектов',
          createFirst: 'Создайте первый проект, чтобы начать работу',
          stats: {
            tasks: '{{count}} задач',
            epics: '{{count}} эпиков',
            sprints: '{{count}} спринтов',
          },
          archive: 'Архивировать',
          restore: 'Восстановить',
          delete: 'Удалить',
          deleteConfirm: {
            title: 'Удалить проект',
            description: 'Вы уверены, что хотите навсегда удалить "{{name}}"? Это действие нельзя отменить, все задачи, эпики и данные будут потеряны.',
            confirm: 'Удалить проект',
          },
          archived: 'Архивные проекты',
          favorite: 'В избранное',
          manageChannels: 'Управление каналами',
          manageMembers: 'Управление участниками',
          channels: {
            description: 'Управление каналами связи для этого проекта',
            assigned: 'Привязанные каналы',
            add: 'Добавить канал',
            selectToAdd: 'Выберите канал для добавления в проект:',
            noAvailable: 'Больше нет доступных каналов',
            noAssigned: 'Каналы ещё не привязаны',
            global: 'Глобальные каналы',
            autoIncluded: 'Автоматически',
            globalDescription: 'Эти каналы доступны во всех проектах',
            noGlobal: 'Глобальные каналы не настроены',
            helpTitle: 'Как работают каналы',
            helpText: 'Привязанные каналы фильтруют входящие сообщения для этого проекта. Глобальные каналы видны во всех проектах.',
          },
          members: {
            title: 'Участники проекта',
            description: 'Управляйте доступом к этому проекту',
            add: 'Добавить участника',
            confirmAdd: 'Добавить',
            selectUser: 'Выберите пользователя',
            selectUserPlaceholder: 'Выберите участника команды',
            selectRole: 'Выберите роль',
            empty: 'Участники ещё не добавлены',
            readOnlyHint: 'Только владельцы и администраторы могут управлять участниками.',
            remove: 'Удалить участника',
            errors: {
              load: 'Не удалось загрузить участников проекта. Попробуйте ещё раз.',
            },
            roles: {
              owner: 'Владелец',
              admin: 'Администратор',
              member: 'Участник',
              viewer: 'Наблюдатель',
            },
          },
        },
        integrations: {
          title: 'Подключённые приложения',
          description: 'Управляйте подключёнными приложениями и сервисами.',
          connected: 'Подключено',
          notConnected: 'Не подключено',
          connect: 'Подключить',
          disconnect: 'Отключить',
          google: 'Gmail',
          googleDesc: 'Синхронизировать Gmail',
          microsoft: 'Microsoft 365',
          microsoftDesc: 'Синхронизировать Outlook почту и календарь',
          whatsapp: 'WhatsApp',
          whatsappDesc: 'Подключить WhatsApp Business',
          telegram: 'Telegram',
          telegramDesc: 'Подключить Telegram‑бота',
          linkedin: 'LinkedIn',
          linkedinDesc: 'Подключить сообщения LinkedIn',
          hubspot: 'HubSpot',
          hubspotDesc: 'Синхронизировать контакты, компании и сделки с HubSpot CRM',
          syncNow: 'Синхронизировать',
          aimfox: 'AimFox',
          aimfoxDesc: 'Автоматизация LinkedIn outreach',
          configure: 'Настроить',
          manageMessages: 'Открыть сообщения',
          slackDesc: 'Получать уведомления в Slack',
          zoomDesc: 'Создавать встречи автоматически',
        },
        aimfox: {
          title: 'Настройка AimFox',
          description: 'Подключите аккаунт AimFox для автоматизации LinkedIn outreach.',
          apiKey: 'API ключ',
          apiKeyPlaceholder: 'Введите API ключ AimFox',
          apiKeySet: '••••••••••••',
          workspaceId: 'ID рабочего пространства',
          workspaceIdPlaceholder: 'Введите ID рабочего пространства',
          automationTitle: 'Настройки автоматизации',
          autoCreateContacts: 'Автоматически создавать контакты из лидов',
          autoCreateDeals: 'Автоматически создавать сделки при ответе',
          syncLabels: 'Синхронизировать метки как теги',
          syncNotes: 'Синхронизировать заметки в контакты',
          pauseOnWon: 'Приостановить кампанию при выигрыше сделки',
          removeOnLost: 'Удалить из кампании при проигрыше сделки',
          testConnection: 'Проверить подключение',
          testSuccess: 'Подключение успешно',
          testFailed: 'Подключение не удалось',
          campaigns: 'Кампании',
          pauseCampaign: 'Пауза',
          resumeCampaign: 'Возобновить',
        },
        ai: {
          keys: {
            title: 'Ключи AI API',
            description: 'Подключите AI‑провайдеров для маршрутизации моделей.',
            apiKey: 'API ключ',
            baseUrl: 'Base URL',
            model: 'Модель',
            selectModel: 'Выберите модель',
            recommended: 'рекомендуется',
            recommendedModel: 'Рекомендуется',
            configured: 'Настроено',
            notConfigured: 'Не настроено',
            save: 'Сохранить ключи',
            remove: 'Удалить',
            saveError: 'Не удалось сохранить ключи. Попробуйте ещё раз.',
            removeConfirm: {
              title: 'Удалить API ключ',
              description: 'Вы уверены, что хотите удалить API ключ {{provider}}? Вам придётся ввести его снова, чтобы использовать этого провайдера.',
              confirm: 'Удалить ключ',
            },
          },
          models: {
            title: 'Маршрутизация моделей',
            description: 'Выберите, какая модель отвечает за каждую AI‑задачу.',
            defaultModel: 'Модель по умолчанию',
            selectModel: 'Выберите модель',
            notConfigured: 'провайдер не настроен',
            save: 'Сохранить маршрутизацию',
            saving: 'Сохраняем маршрутизацию...',
            saveError: 'Не удалось сохранить маршрутизацию. Попробуйте ещё раз.',
          },
          providers: {
            kimi: {
              name: 'Kimi',
              keyPlaceholder: 'sk-...',
              baseUrlPlaceholder: 'https://api.moonshot.ai/v1',
            },
            openai: {
              name: 'OpenAI',
              keyPlaceholder: 'sk-...',
              baseUrlPlaceholder: 'https://api.openai.com/v1',
            },
            gemini: {
              name: 'Gemini',
              keyPlaceholder: 'AIza...',
              baseUrlPlaceholder: 'https://generativelanguage.googleapis.com',
            },
            anthropic: {
              name: 'Anthropic',
              keyPlaceholder: 'sk-ant-...',
              baseUrlPlaceholder: 'https://api.anthropic.com/v1',
            },
          },
          tasks: {
            entity_extraction: {
              title: 'Извлечение сущностей',
              description: 'Извлекает структурированные сущности из писем и заметок.',
            },
            summarization: {
              title: 'Суммаризация',
              description: 'Сводки длинных переписок и тредов.',
            },
            draft_generation: {
              title: 'Генерация черновиков',
              description: 'Генерация ответов и черновиков сообщений.',
            },
            classification: {
              title: 'Классификация',
              description: 'Классификация интентов и сообщений.',
            },
            meeting_preparation: {
              title: 'Подготовка к встречам',
              description: 'Подготовка брифов перед встречами.',
            },
            meeting_summary: {
              title: 'Сводка встреч',
              description: 'Сводки встреч и извлечение action items.',
            },
            followup_detection: {
              title: 'Определение фоллоу‑апов',
              description: 'Выявляет фоллоу‑апы и напоминания.',
            },
            task_extraction: {
              title: 'Извлечение задач',
              description: 'Извлекает задачи из писем и переписок.',
            },
            smart_notifications: {
              title: 'Умные уведомления',
              description: 'Интеллектуальные сводки уведомлений.',
            },
            style_adaptation: {
              title: 'Адаптация стиля',
              description: 'Подстраивает тон и стиль ответов.',
            },
            assistant_chat: {
              title: 'Чат ассистента',
              description: 'Запросы к AI‑ассистенту.',
            },
            messaging_reply: {
              title: 'Ответы в мессенджерах',
              description: 'Генерация ответов для мессенджеров.',
            },
            insight_generation: {
              title: 'Генерация инсайтов',
              description: 'Генерация аналитических инсайтов и рекомендаций.',
            },
          },
          skills: {
            title: 'Каталог AI‑навыков',
            description: 'Включайте и отключайте инструменты, доступные ассистенту в этом рабочем пространстве.',
            empty: 'Навыки недоступны.',
            enabled: 'Включено',
            disabled: 'Выключено',
            deprecated: 'Устарело',
            uncategorized: 'Без категории',
            filters: {
              label: 'Фильтр',
              all: 'Все категории',
              categoryPlaceholder: 'Категория',
              searchLabel: 'Поиск навыков',
              searchPlaceholder: 'Поиск навыков...',
              empty: 'Нет навыков по выбранным фильтрам.',
            },
            categories: {
              email: 'Почта',
              pipeline: 'Пайплайн',
              deal: 'Сделки',
              contact: 'Контакты',
              interaction: 'Взаимодействия',
              task: 'Задачи',
              onboarding: 'Онбординг',
              analysis: 'Аналитика',
              meeting: 'Встречи',
              messaging: 'Сообщения',
              tasks: 'Задачи',
              account: 'Аккаунты',
              communications: 'Коммуникации',
              crm: 'CRM',
              sales: 'Продажи',
              analytics: 'Аналитика',
              automation: 'Автоматизация',
              productivity: 'Продуктивность',
              ops: 'Операции',
              marketing: 'Маркетинг',
              support: 'Поддержка',
              integrations: 'Интеграции',
              system: 'Система',
            },
            permissions: 'Права',
            cacheTtl: 'Кэш {{seconds}}с',
            version: 'v{{version}}',
            risk: {
              read_only: 'Только чтение',
              write: 'Запись',
              external_write: 'Внешняя запись',
            },
            sensitivity: {
              public: 'Публичные',
              internal: 'Внутренние',
              confidential: 'Конфиденциальные',
              restricted: 'Ограниченные',
            },
          },
        },
      },
      actions: {
        title: 'Действия',
        subtitle: 'Управляйте задачами и фоллоу‑апами',
        newAction: 'Новое действие',
        createAction: 'Создать действие',
        filter: 'Фильтр',
        searchPlaceholder: 'Поиск действий...',
        loadingShort: '...',
        status: {
          todo: 'К выполнению',
          inProgress: 'В работе',
          done: 'Готово',
          dismissed: 'Отклонено',
          snoozed: 'Отложено',
        },
        filters: {
          all: 'Все',
        },
        priority: {
          high: 'Высокий',
          medium: 'Средний',
          low: 'Низкий',
        },
        type: {
          follow_up: 'Фоллоу‑ап',
          upsell: 'Апсейл',
          at_risk: 'Под риском',
          support: 'Поддержка',
          onboarding: 'Онбординг',
        },
        source: {
          email: 'Email',
          whatsapp: 'WhatsApp',
          telegram: 'Telegram',
          manual: 'Вручную',
        },
        menu: {
          edit: 'Редактировать',
          markTodo: 'Вернуть в очередь',
          startProgress: 'Взять в работу',
          markDone: 'Завершить',
          dismiss: 'Отклонить',
        },
        sheet: {
          createTitle: 'Новое действие',
          createDescription: 'Создайте новое действие',
          editTitle: 'Редактирование',
          editDescription: 'Обновите детали действия',
        },
        form: {
          title: 'Название',
          titlePlaceholder: 'Что нужно сделать?',
          description: 'Описание',
          descriptionPlaceholder: 'Добавьте детали...',
          actionType: 'Тип',
          source: 'Источник',
          priority: 'Приоритет',
          status: 'Статус',
          dueDate: 'Срок',
        },
        empty: {
          title: 'Действий не найдено',
          searchHint: 'Попробуйте изменить поиск или фильтры',
          cta: 'Создайте новое действие, чтобы начать',
        },
        errors: {
          loadFailed: 'Не удалось загрузить действия. Попробуйте ещё раз.',
        },
        labels: {
          assignee: 'Ответственный',
        },
        badges: {
          ai: 'ИИ',
          aiWithActor: 'ИИ · {{actor}}',
          aiTooltip: 'Сгенерировано Turia AI',
          aiTooltipNoActor: 'Сгенерировано ИИ',
        },
      },
      authVerify: {
        loading: {
          title: 'Проверяем вход...',
          subtitle: 'Подождите, пока мы проверяем ссылку для входа',
        },
        success: {
          title: 'Вход выполнен!',
          subtitle: 'Вы вошли в систему. Перенаправляем...',
        },
        error: {
          title: 'Не удалось подтвердить',
          subtitle: 'Ссылка для входа могла истечь. Запросите новую.',
          cta: 'Запросить новую ссылку',
        },
        errors: {
          missingToken: 'Отсутствует токен подтверждения',
          verificationFailed: 'Не удалось подтвердить',
          unableToVerify: 'Не удалось подтвердить ссылку для входа. Попробуйте ещё раз.',
        },
      },
      auth: {
        login: {
          welcome: 'Добро пожаловать в',
          brandPrimary: 'Run',
          brandSecondary: 'Rate',
          emailPlaceholder: 'Email адрес',
          submit: 'Войти или зарегистрироваться',
          loading: 'Загрузка...',
          logoAlt: 'Логотип RunRate',
          requestIdSuffix: ' (ref: {{id}})',
          messages: {
            checkEmail: 'Проверьте почту — мы отправили ссылку для входа.',
          },
          errors: {
            network: 'Ошибка сети. Проверьте подключение и попробуйте ещё раз.',
            tooManyAttempts: 'Слишком много попыток. Попробуйте позже.',
            sendFailed: 'Не удалось отправить ссылку для входа. Попробуйте ещё раз.',
            httpStatus: ' (HTTP {{status}})',
          },
        },
      },
      footer: {
        copyright: '© {{year}} RunRate',
        privacy: 'Политика конфиденциальности и условия использования',
      },
      companies: {
        title: 'Компании',
        entity: 'Компания',
        count: '{{count}} компаний',
        filter: 'Фильтр',
        addCompany: 'Добавить компанию',
        searchPlaceholder: 'Поиск компаний...',
        actions: {
          viewDetails: 'Детали',
        },
        quickActions: {
          email: 'Письмо',
          emailTooltip: 'Отправить письмо компании',
          deal: 'Сделка',
          dealTooltip: 'Создать сделку',
        },
        table: {
          name: 'Название',
          industry: 'Отрасль',
          size: 'Размер',
          location: 'Местоположение',
          deals: 'Сделки',
        },
        deals: {
          count: '{{count}} сделок',
          totalValue: '{{value}} всего',
        },
        empty: {
          title: 'Компании не найдены',
          searchHint: 'Попробуйте изменить поиск или добавьте новую компанию.',
          cta: 'Начните с добавления первой компании.',
        },
        errors: {
          loadFailed: 'Не удалось загрузить компании. Попробуйте ещё раз.',
        },
      },
      contacts: {
        title: 'Контакты',
        entity: 'Контакт',
        count: '{{count}} контактов',
        filter: 'Фильтр',
        addContact: 'Добавить контакт',
        searchPlaceholder: 'Поиск контактов...',
        table: {
          name: 'Имя',
          contactInfo: 'Контактные данные',
          tags: 'Теги',
          lastUpdated: 'Обновлено',
        },
        actions: {
          viewProfile: 'Профиль',
          sendEmail: 'Отправить email',
          call: 'Позвонить',
        },
        empty: {
          title: 'Контакты не найдены',
          searchHint: 'Попробуйте изменить поиск или добавьте новый контакт.',
          cta: 'Начните с добавления первого контакта.',
        },
        errors: {
          loadFailed: 'Не удалось загрузить контакты. Попробуйте ещё раз.',
        },
        duplicates: {
          title: 'Возможные дубликаты',
          badge: '{{count}} дубликатов',
          found: 'Найдено {{count}} групп потенциальных дубликатов',
          none: 'Дубликаты не найдены',
          allClean: 'Все контакты уникальны!',
          merge: 'Объединить',
          reason: {
            email: 'Одинаковый email',
            name: 'Одинаковое имя',
            phone: 'Одинаковый телефон',
          },
        },
        time: {
          minutesAgo: '{{count}} мин. назад',
          hoursAgo: '{{count}} ч назад',
          daysAgo: '{{count}} дн. назад',
          weeksAgo: '{{count}} нед. назад',
        },
      },
      contactDetail: {
        actions: {
          backToRecords: 'Назад к списку',
          backToContacts: 'Назад к контактам',
        },
        errors: {
          notFound: 'Контакт не найден',
          notFoundDescription: 'Контакт, который вы ищете, не существует.',
        },
        tabs: {
          timeline: 'Таймлайн',
          emails: 'Письма',
          tasks: 'Задачи',
          deals: 'Сделки',
          notes: 'Заметки',
          linkedin: 'LinkedIn',
        },
        insights: {
          title: 'Turia Insights',
          next: 'Дальше:',
        },
        linkedin: {
          empty: {
            title: 'Нет данных LinkedIn',
            description: 'Подключите AimFox, чтобы видеть данные LinkedIn для этого контакта.',
          },
          status: {
            connected: 'Подключён',
            pending: 'Ожидание',
            declined: 'Отклонён',
          },
          viewProfile: 'Открыть профиль',
          messagesSent: 'Отправлено сообщений',
          messagesReceived: 'Получено сообщений',
          responseRate: 'Процент ответов',
          campaigns: 'Кампании',
          lastMessage: 'Последнее сообщение',
        },
        timeline: {
          title: 'История активности',
          addNote: 'Добавить заметку',
          errors: {
            loadFailed: 'Не удалось загрузить активность. Попробуйте ещё раз.',
          },
          empty: {
            title: 'Активности пока нет',
            description: 'Активность появится здесь по мере взаимодействий с контактом',
          },
        },
        emails: {
          errors: {
            loadFailed: 'Не удалось загрузить письма. Попробуйте ещё раз.',
          },
          empty: {
            title: 'Писем пока нет',
            description: 'Письма появятся здесь после синхронизации',
          },
        },
        files: {
          title: 'Файлы',
          open: 'Открыть',
          errors: {
            loadFailed: 'Не удалось загрузить файлы. Попробуйте ещё раз.',
          },
        },
        tasks: {
          errors: {
            loadFailed: 'Не удалось загрузить задачи. Попробуйте ещё раз.',
          },
          empty: {
            title: 'Задач пока нет',
            description: 'Создайте задачу, чтобы отслеживать фоллоу‑апы',
          },
        },
        deals: {
          errors: {
            loadFailed: 'Не удалось загрузить сделки. Попробуйте ещё раз.',
          },
          noCloseDate: 'Нет даты закрытия',
          empty: {
            title: 'Сделок пока нет',
            description: 'Здесь можно отслеживать продажи',
          },
        },
        notes: {
          errors: {
            loadFailed: 'Не удалось загрузить заметки. Попробуйте ещё раз.',
          },
          empty: {
            title: 'Заметок пока нет',
            description: 'Добавляйте заметки, чтобы помнить важные детали',
          },
        },
      },
      messages: {
        title: 'Сообщения',
        searchPlaceholder: 'Поиск переписок...',
        newMessage: 'Новое сообщение',
        send: 'Отправить',
        inputPlaceholder: 'Введите сообщение...',
        conversationActive: 'Переписка активна',
        noMessagesYet: 'Сообщений пока нет',
        conversationFallback: 'Переписка {{id}}',
        accounts: 'Аккаунты',
        activeAccountLabel: 'Аккаунт inbox',
        accountStatus: {
          connected: 'Подключен',
          notConnected: 'Не подключен',
        },
        empty: {
          noAccountsTitle: 'Нет подключённых мессенджеров',
          noAccountsDescription: 'Подключите WhatsApp или Telegram, чтобы начать переписку',
          noConversationsTitle: 'Переписок пока нет',
          noConversationsDescription: 'Начните диалог с вашими контактами',
          noMessagesInConversation: 'В этой переписке пока нет сообщений.',
          selectTitle: 'Выберите переписку',
          selectDescription: 'Выберите диалог из списка или создайте новый, чтобы начать общение',
        },
        errors: {
          loadConversations: 'Не удалось загрузить переписки. Попробуйте ещё раз.',
          loadMessages: 'Не удалось загрузить сообщения. Попробуйте ещё раз.',
        },
        telegramAuth: {
          title: 'Подключить аккаунт Telegram',
          subtitle: 'Используйте личный Telegram для синхронизации диалогов.',
          connect: 'Подключить',
          manage: 'Управлять',
          sendCode: 'Отправить код',
          resendCode: 'Отправить снова',
          verifyCode: 'Подтвердить код',
          submitPassword: 'Отправить пароль',
          phoneLabel: 'Номер телефона',
          phonePlaceholder: '+34 600 000 000',
          displayNameLabel: 'Отображаемое имя',
          displayNamePlaceholder: 'Telegram аккаунт',
          codeLabel: 'Код входа',
          codePlaceholder: '12345',
          passwordLabel: 'Пароль 2FA',
          passwordPlaceholder: 'Пароль Telegram',
          connectedNotice: 'Аккаунт Telegram подключен.',
          defaultDisplayName: 'Telegram аккаунт',
          status: {
            connected: 'Подключен',
            notConnected: 'Не подключен',
            codeSent: 'Код отправлен',
            passwordRequired: 'Нужен пароль',
            error: 'Ошибка подключения',
          },
          errors: {
            phoneRequired: 'Номер телефона обязателен',
            codeRequired: 'Код обязателен',
            passwordRequired: 'Пароль обязателен',
            accountMissing: 'Аккаунт Telegram не найден',
            generic: 'Не удалось подключить Telegram',
          },
        },
      },
      supportPipeline: {
        title: 'Поддержка',
        subtitle: 'Управляйте обращениями клиентов',
        newTicket: 'Новый тикет',
        createTicket: 'Создать тикет',
        filter: 'Фильтр',
        retry: 'Повторить',
        searchPlaceholder: 'Поиск тикетов...',
        status: {
          new: 'Новый',
          inProgress: 'В работе',
          waitingCustomer: 'Ожидает клиента',
          waitingInternal: 'Ожидает внутри',
          resolved: 'Решено',
          closed: 'Закрыто',
        },
        priority: {
          low: 'Низкий',
          medium: 'Средний',
          high: 'Высокий',
          urgent: 'Срочный',
        },
        columns: {
          new: 'Новые',
          inProgress: 'В работе',
          waitingCustomer: 'Ожидают клиента',
          waitingInternal: 'Ожидают внутри',
          resolved: 'Решённые',
        },
        labels: {
          unknownRequester: 'Неизвестно',
        },
        empty: {
          column: 'Нет тикетов',
          title: 'Нет обращений',
          description: 'Когда клиенты отправят запросы, они появятся здесь',
        },
        errors: {
          loadFailed: 'Не удалось загрузить тикеты. Попробуйте ещё раз.',
          unknown: 'Неизвестная ошибка',
        },
        stats: {
          open: 'Открытые',
          inProgress: 'В работе',
          waitingCustomer: 'Ожидают',
          slaBreached: 'SLA нарушен',
          avgResponse: 'Среднее время',
          total: 'Всего',
        },
        filters: {
          allStatuses: 'Все статусы',
          allPriorities: 'Все приоритеты',
          allCategories: 'Все категории',
          allAssignees: 'Все исполнители',
          assignedToMe: 'Назначенные мне',
        },
        table: {
          ticket: 'Тикет',
          status: 'Статус',
          priority: 'Приоритет',
          category: 'Категория',
          assignee: 'Исполнитель',
          source: 'Источник',
          created: 'Создан',
          assigned: 'Назначен',
          unassigned: 'Не назначен',
        },
        pagination: {
          page: 'Страница {{page}}',
          pageOf: 'Страница {{page}} из {{total}}',
          previous: 'Назад',
          next: 'Вперёд',
        },
        view: {
          board: 'Доска',
          table: 'Таблица',
        },
        loadingTickets: 'Загрузка тикетов...',
      },
      oauthCallback: {
        providers: {
          google: 'Google',
          microsoft: 'Microsoft',
          email: 'почта',
        },
        loading: {
          title: 'Подключаем почту...',
          subtitle: 'Настраиваем уведомления {{provider}}',
        },
        success: {
          title: 'Почта подключена!',
          subtitle: 'Аккаунт {{provider}} успешно подключен.',
        },
        warning: {
          title: 'Подключено частично',
          subtitle: 'Аккаунт {{provider}} подключен, но уведомления почты не удалось включить.',
        },
        error: {
          title: 'Ошибка подключения',
        },
        actions: {
          continueToInbox: 'Перейти во Входящие',
          retryNotifications: 'Повторить настройку уведомлений',
          continueAnyway: 'Продолжить',
          tryAgain: 'Попробовать снова',
          skip: 'Пропустить',
        },
        errors: {
          authFailed: 'OAuth авторизация не удалась',
          invalidState: 'Некорректное состояние колбэка',
          generic: 'Не удалось подключить почту. Попробуйте еще раз.',
          watchNotSupported: 'Уведомления не поддерживаются для {{provider}}.',
        },
        watchFailed: 'Не удалось настроить уведомления почты',
      },
      oauth: {
        connecting: 'Подключаем...',
        success: 'Успешно подключено!',
        error: 'Ошибка подключения',
        warning: 'Подключено частично',
        retry: 'Повторить',
        goToInbox: 'Перейти во Входящие',
      },
      header: {
        projectScope: {
          all: 'Все проекты',
          manage: 'Управлять проектами',
        },
        themeToggle: 'Переключить тему',
        user: {
          settings: 'Настройки',
          logout: 'Выйти',
        },
      },
      commandPalette: {
        trigger: 'Поиск',
        placeholder: 'Поиск команд...',
        input: 'Поиск команд...',
        empty: 'Ничего не найдено.',
        loading: 'Загрузка...',
        noContacts: 'Контакты не найдены',
        noCompanies: 'Компании не найдены',
        noProjects: 'Проекты не найдены',
        group: {
          recent: 'Недавние',
          contacts: 'Контакты',
          companies: 'Компании',
          projects: 'Проекты',
          scope: 'Скоуп проекта',
        },
        scope: {
          all: 'Все проекты',
        },
      },
      invite: {
        loading: 'Загрузка приглашения...',
        title: 'Вас приглашают!',
        subtitle: '{{name}} приглашает вас в команду',
        joiningOrg: 'Вы присоединитесь к',
        invitedAs: 'Приглашены как',
        accept: 'Принять приглашение',
        notExpected: 'Не ожидали этого?',
        ignoreInvite: 'Игнорировать приглашение',
        securityNote: 'Эта ссылка уникальна и может быть использована только один раз.',
        success: 'Добро пожаловать в команду!',
        successDescription: 'Ваше приглашение успешно принято.',
        redirecting: 'Перенаправляем на главную...',
        invalid: 'Недействительное приглашение',
        invalidDescription: 'Эта ссылка недействительна или уже была использована.',
        goToLogin: 'Войти',
        expired: 'Приглашение истекло',
        expiredDescription: 'Срок действия приглашения истёк. Попросите администратора отправить новое.',
        contactAdmin: 'Обратитесь к администратору за новым приглашением.',
        acceptError: 'Не удалось принять приглашение. Попробуйте снова.',
      },
      common: {
        back: 'Назад',
        loading: 'Загрузка...',
        error: 'Ошибка',
        success: 'Успех',
        cancel: 'Отмена',
        confirm: 'Подтвердить',
        save: 'Сохранить',
        delete: 'Удалить',
        edit: 'Редактировать',
        close: 'Закрыть',
        create: 'Создать',
        add: 'Добавить',
        comingSoon: 'Скоро',
        unavailable: 'Недоступно',
        settingsLinks: {
          ai: 'Открыть Настройки → AI',
          integrations: 'Открыть Настройки → Интеграции',
        },
      },
      blocks: {
        blockedBy: 'Заблокировано',
        blocks: 'Блокирует',
        blocked: 'Заблокировано',
        noBlocks: 'Эта задача не блокирует другие',
        addBlock: 'Добавить блокирующую задачу',
        searchTasks: 'Поиск задач...',
        noTasksFound: 'Задачи не найдены',
        blockedWarning: 'Нельзя начать задачу пока не завершены блокирующие',
        removeBlock: 'Убрать блокировку',
      },
      estimates: {
        label: 'Оценка',
        points: 'очков',
        select: 'Указать оценку...',
        clear: 'Убрать оценку',
        effort: {
          trivial: 'Тривиально - быстрый фикс',
          small: 'Мало - пара часов',
          medium: 'Средне - полдня',
          large: 'Много - целый день',
          xlarge: 'Очень много - несколько дней',
          epic: 'Эпик - требует декомпозиции',
        },
      },
      cycles: {
        label: 'Цикл',
        none: 'Без цикла',
        select: 'Выберите цикл...',
        search: 'Поиск циклов...',
        empty: 'Циклы не найдены',
        create: 'Создать цикл',
        edit: 'Редактировать цикл',
        createDescription: 'Создайте новый цикл для организации работы в спринты.',
        editDescription: 'Обновите детали и настройки цикла.',
        deleteTitle: 'Удалить цикл',
        deleteDescription: 'Вы уверены, что хотите удалить "{{name}}"? Задачи в этом цикле будут откреплены.',
        duration: '{{days}} дней',
        progress: 'Прогресс',
        tasks: 'задач',
        points: 'очков',
        active: 'Активные',
        planned: 'Запланированные',
        completed: 'Завершённые',
        status: {
          planned: 'Запланирован',
          active: 'Активен',
          completed: 'Завершён',
          cancelled: 'Отменён',
        },
        fields: {
          name: 'Название',
          namePlaceholder: 'Спринт 1, Q1 2026...',
          goal: 'Цель',
          goalPlaceholder: 'Чего вы хотите достичь в этом цикле?',
          startDate: 'Дата начала',
          endDate: 'Дата окончания',
          status: 'Статус',
          description: 'Описание',
          descriptionPlaceholder: 'Дополнительные заметки о цикле...',
        },
      },
      confirmSheet: {
        subtitle: 'Подтвердите действие',
      },
      deleteConfirmSheet: {
        title: 'Удалить {{entity}}',
        subtitle: 'Это действие нельзя отменить.',
        description:
          'Вы уверены, что хотите удалить "{{name}}"? Будут удалены {{entity}} и все связанные данные.',
      },
      actionCard: {
        types: {
          followUp: 'Фоллоу‑ап',
          upsell: 'Апселл‑возможность',
          atRisk: 'В зоне риска',
          support: 'Поддержка',
          onboarding: 'Онбординг',
        },
        reasoningPrefix: '💡',
        dueLabel: 'Срок:',
        actions: {
          complete: 'Выполнить',
          snooze: 'Отложить',
          dismiss: 'Скрыть',
        },
      },
      insightCard: {
        types: {
          skepticConversion: 'Сомневающиеся',
          silentComplaint: 'Скрытая жалоба',
          salesCoaching: 'Коучинг продаж',
          competitiveIntel: 'Конкурентная аналитика',
        },
        recommendedActions: 'Рекомендуемые действия:',
        more: '+{{count}} ещё...',
        confidence: 'Уверенность:',
        viewDetails: 'Подробнее',
      },
      mainLayout: {
        search: 'Поиск',
        workspace: {
          personal: 'Личный',
        },
        projects: {
          allProjects: 'Все проекты',
          noProjects: 'Пока нет проектов',
        },
        sidebar: {
          collapse: 'Свернуть',
          expand: 'Развернуть',
        },
        nav: {
          home: 'Главная',
          actions: 'Действия',
          inbox: 'Входящие',
          calendar: 'Календарь',
          booking: 'Запись',
          kanban: 'Канбан',
          tasks: 'Задачи',
          pages: 'Страницы',
          analytics: 'Аналитика',
          pipelines: 'Воронки',
          sales: 'Продажи',
          support: 'Поддержка',
          records: 'Записи',
          contacts: 'Контакты',
          companies: 'Компании',
          saasAdmin: 'SaaS Админ',
          settings: 'Настройки',
        },
        theme: {
          light: 'Светлая тема',
          dark: 'Тёмная тема',
        },
        user: {
          logout: 'Выйти',
        },
      },
      errorBoundary: {
        title: 'Что-то пошло не так',
        subtitle: 'Извините за неудобства. Приложение столкнулось с непредвиденной ошибкой.',
        devDetails: 'Детали ошибки (только для разработки)',
        componentStack: 'Стек компонентов:',
        actions: {
          tryAgain: 'Попробовать снова',
          reload: 'Перезагрузить страницу',
        },
        supportHint: 'Если проблема сохраняется, обратитесь в поддержку.',
      },
      progressTimeline: {
        completed: 'Завершено',
        days: '{{count}}д',
      },
      pagination: {
        ariaLabel: 'Пагинация',
        previous: 'Предыдущая страница',
        next: 'Следующая страница',
        page: 'Страница {{page}}',
        info: 'Показано {{start}}–{{end}} из {{total}}',
      },
      project: {
        onboarding: {
          title: 'Новый проект',
          nameRequired: 'Введите название проекта',
          errorFailed: 'Не удалось создать проект. Попробуйте снова.',
          createProject: 'Создать проект',
          steps: {
            details: 'Детали',
            channels: 'Каналы',
            finish: 'Готово',
          },
          step1: {
            title: 'Детали проекта',
            description: 'Дайте проекту название и настройте его внешний вид.',
            nameLabel: 'Название проекта',
            namePlaceholder: 'напр., RunDesk, Маркетинговая кампания',
            descriptionLabel: 'Описание (опционально)',
            descriptionPlaceholder: 'Краткое описание проекта',
            colorLabel: 'Цвет',
            iconLabel: 'Иконка',
            preview: 'Предпросмотр',
            untitled: 'Безымянный проект',
            noDescription: 'Без описания',
          },
          step2: {
            title: 'Подключить каналы',
            description: 'Выберите каналы связи, которые будут привязаны к этому проекту.',
            email: 'Почта',
            noEmail: 'Ещё нет подключённых почтовых аккаунтов',
            connectNewEmail: 'Подключить новый почтовый аккаунт',
            global: 'Доступен во всех проектах',
            specific: 'Привязан к проектам',
            tip: 'Вы можете добавить больше каналов позже в настройках проекта',
          },
          step3: {
            title: 'Готов к запуску',
            description: 'Проверьте настройки проекта и создайте его.',
            connectedChannels: 'Подключённые каналы:',
            ready: 'Всё готово! Нажмите "Создать проект" чтобы начать.',
          },
        },
      },
    },
  },
}

// Check if we're in a test environment
const isTest = import.meta.env?.MODE === 'test' || process.env.NODE_ENV === 'test'
const supportedLanguages = ['en', 'ru']

const getInitialLanguage = () => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem('language') || window.localStorage.getItem('lang')
    if (stored && supportedLanguages.includes(stored)) {
      return stored
    }
    const browser = window.navigator?.language?.split('-')[0]
    if (browser && supportedLanguages.includes(browser)) {
      return browser
    }
  }
  return 'en'
}

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  // In tests, initialize immediately
  initImmediate: !isTest,
  // Don't load resources asynchronously in tests
  ...(isTest && {
    react: {
      useSuspense: false,
    },
  }),
})

export default i18n
